/**
 * VEDANGJYOTISHAM V6.0 — DASHA ↔ GOCHAR SYNTHESIS ENGINE
 * 
 * Master analytical synthesis layer sitting above the verified Vimshottari and Gochar engines.
 * 
 * Core Architectural Invariants:
 * 1. ZERO duplicate calculation engines: Consumes verified natal chart (D1, Vargas, Dashas)
 *    and verified transit ephemeris (calculateLivePlanetaryEphemeris).
 * 2. Two Analytical Lenses:
 *    - Mode A (Gochar over Vimshottari): "Which Gochar movements trigger/modify Dasha activations?"
 *    - Mode B (Vimshottari over Gochar): "Under current Gochar, which Vimshottari factors stimulate houses?"
 * 3. Evidence-First House Activation Model: 12-house activation map with transparent verification.
 * 4. Transparent Contradiction Surfacing & Acharya Pause protocol.
 * 5. Deterministic, chronological lifetime life-cycle horizon.
 */

import { CompleteKundali, Language, PlanetName, ZodiacSign, PlanetPosition } from '../types/kundali';
import {
  calculateLivePlanetaryEphemeris,
  ZODIAC_LORDS,
  NAKSHATRAS,
  getSavByBhava,
  getBavByBhava,
  calculateKakshyaTransitInfo,
  getSpecialDrishtiHouses
} from './vedicEngine';
import { getSignLord } from './v600/lifePurposeEngine';
import { calculatePrastaraTables } from './ashtakvargaAdvancedEngine';
import { calculateShadbala } from './shadbalaEngine';
import {
  DashaGocharLens,
  CorroborationLevel,
  ConfidenceRating,
  HouseActivationEvidence,
  SynthesisDashaContext,
  TransitPlanetImpact,
  SynthesisGocharContext,
  LifetimeCyclePhase,
  DashaGocharSynthesisResult,
  CommonNormalizedEvidence,
  DashaPeriodComparisonResult,
  DashaPeriodComparisonItem,
  PredictiveEvidenceItem,
  CanonicalPredictiveResult,
  TimingState,
  AcharyaPauseType
} from '../types/dashaGocharSynthesis';
import {
  PredictiveEventWindow,
  PredictiveEventType,
  ProbabilityLevel,
  EvidenceStatus,
  TriangulationResult
} from '../types/v519';
import { EVENT_RULE_REGISTRY, predictAllLifeEvents } from './v519/eventPredictionEngine';
import { triangulateEvent } from './v519/triangulationEngine';
import { DynamicAstrologicalAnalysis, generateDynamicAstrologicalAnalysis } from './dynamicAnalysisEngine';
import { AstrologicalEvidenceItem, PredictionFingerprint } from '../types/predictionDifferentiation';
import { LifeArea } from './integratedProfileGenerator';
import {
  extractAstrologicalEvidence,
  generatePredictionFingerprint,
  resolveDominanceAndConflict
} from './predictionDifferentiationEngine';

const ZODIAC_SIGNS: ZodiacSign[] = [
  'Aries', 'Taurus', 'Gemini', 'Cancer',
  'Leo', 'Virgo', 'Libra', 'Scorpio',
  'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

function getZodiacIndex(sign: string): number {
  const idx = ZODIAC_SIGNS.indexOf(sign as ZodiacSign);
  return idx >= 0 ? idx : 0;
}

function calculateSignDistance(fromSign: ZodiacSign, targetSign: ZodiacSign): number {
  const fIdx = getZodiacIndex(fromSign);
  const tIdx = getZodiacIndex(targetSign);
  return ((tIdx - fIdx + 12) % 12) + 1;
}

// Canonical Drishti Rules (Parashari System) re-exported from authoritative vedicEngine
export { getSpecialDrishtiHouses };

export interface SynthesisEvaluationOptions {
  lens?: DashaGocharLens;
  selectedMahaPlanet?: PlanetName;
  selectedAntarPlanet?: PlanetName;
  selectedPratPlanet?: PlanetName;
  customStartDate?: string;
  customEndDate?: string;
  transitDate?: Date | string;
  analysisDateTime?: Date | string;
  isLifetimeMode?: boolean;
}

/**
 * Parashari Natal Promise Evaluator
 * Integrates Bhavesh dignity, Kendra/Trikona placement, natural benefic/malefic occupancy,
 * Drishti (Jupiter, Venus, own lord), Layer 6 SAV bindus, and Layer 7 Shadbala / Bhava Bala.
 */
export function evaluateNatalPromiseForHouse(
  h: number,
  hSign: ZodiacSign,
  hLord: PlanetName,
  kundali: CompleteKundali,
  occupants: PlanetName[],
  aspects: PlanetName[],
  savPoints: number,
  shadbalaData?: any
) {
  const factors: string[] = [];
  const factorsHi: string[] = [];
  let score = 25; // baseline for house existence

  const hLordPl = kundali.planets?.find(p => p.name === hLord);
  const lordDignity = hLordPl?.dignity || 'Neutral';
  const lordHouse = hLordPl?.house || 1;

  // Retrieve Lord Shadbala
  const lordShadbalaItem = shadbalaData?.planets?.find((p: any) => p.planet === hLord);
  const lordShadbalaRatio: number = lordShadbalaItem?.ratio ?? 1.0;
  const lordShadbalaVirupas: number = lordShadbalaItem?.totalVirupas ?? 360;

  // Retrieve Bhava Bala
  const bhavaBalaItem = shadbalaData?.bhavaBala?.find((b: any) => b.houseNumber === h);
  const bhavaBalaRupas: number = bhavaBalaItem?.totalRupas ?? 7.0;
  const bhavaBalaRank: number | undefined = bhavaBalaItem?.rank;

  factors.push(`Ruled by ${hLord} (${lordDignity}) situated in House ${lordHouse}`);
  factorsHi.push(`भावेश ${hLord} (${lordDignity}) ${lordHouse}वें भाव में स्थित`);

  // 1. Lord Dignity
  if (lordDignity === 'Exalted' || lordDignity === 'Own House' || lordDignity === 'Moolatrikona') {
    score += 25;
    factors.push(`Lord ${hLord} holds high dignity (${lordDignity})`);
    factorsHi.push(`भावेश ${hLord} उच्च/स्वराशि/मूलत्रिकोण में प्रतिष्ठित`);
  } else if (lordDignity === 'Friend' || lordDignity === 'Great Friend') {
    score += 15;
  } else if (lordDignity === 'Debilitated') {
    score -= 25;
    factors.push(`Lord ${hLord} is debilitated in natal chart`);
    factorsHi.push(`भावेश ${hLord} जन्म कुंडली में नीचस्थ है`);
  }

  // 2. Lord Placement (Kendra / Trikona / Dusthana)
  const isKendra = [1, 4, 7, 10].includes(lordHouse);
  const isTrikona = [1, 5, 9].includes(lordHouse);
  const isDusthana = [6, 8, 12].includes(lordHouse);

  if (isKendra || isTrikona) {
    score += 15;
    factors.push(`Lord positioned auspiciously in ${isKendra ? 'Kendra' : 'Trikona'} (House ${lordHouse})`);
    factorsHi.push(`भावेश केंद्र/त्रिकोण (${lordHouse}वें भाव) में शुभ स्थित`);
  } else if (isDusthana && h !== lordHouse) {
    score -= 15;
    factors.push(`Lord positioned in Dusthana (House ${lordHouse})`);
    factorsHi.push(`भावेश दुस्थान (${lordHouse}वें भाव) में स्थित`);
  }

  // 3. Occupants
  if (occupants.length > 0) {
    factors.push(`Occupied by ${occupants.join(', ')}`);
    factorsHi.push(`${occupants.join(', ')} द्वारा अधिष्ठित`);

    const naturalBenefics: PlanetName[] = ['Jupiter', 'Venus', 'Mercury'];
    const hasBeneficOccupant = occupants.some(p => naturalBenefics.includes(p));
    if (hasBeneficOccupant) score += 10;
    if (occupants.includes(hLord)) score += 20; // Swakshetra occupant

    const naturalMalefics: PlanetName[] = ['Saturn', 'Mars', 'Rahu', 'Ketu'];
    const hasMaleficOccupant = occupants.some(p => naturalMalefics.includes(p) && p !== hLord);
    if (hasMaleficOccupant) score -= 10;
  }

  // 4. Drishti / Aspects (Benefic modifier contributes points; does NOT unilaterally override deficits)
  if (aspects.length > 0) {
    factors.push(`Aspected by ${aspects.join(', ')}`);
    factorsHi.push(`${aspects.join(', ')} की दृष्टि`);
    if (aspects.includes('Jupiter') || aspects.includes('Venus')) {
      score += 8;
      factors.push(`Benefic Drishti provides supportive modulation`);
    }
    if (aspects.includes(hLord)) score += 12;
    if (aspects.includes('Saturn') || aspects.includes('Mars') || aspects.includes('Rahu')) score -= 8;
  }

  // 5. Layer 7 Bala Modulation (Certified strength modifiers)
  if (lordShadbalaRatio >= 1.15) {
    score += 10;
    factors.push(`Lord ${hLord} possesses strong Shadbala (${lordShadbalaRatio.toFixed(2)} ratio)`);
  } else if (lordShadbalaRatio < 0.85) {
    score -= 10;
    factors.push(`Lord ${hLord} has deficit Shadbala (${lordShadbalaRatio.toFixed(2)} ratio)`);
  }

  if (bhavaBalaRupas >= 8.0) {
    score += 10;
    factors.push(`High Bhava Bala (${bhavaBalaRupas.toFixed(2)} Rupas)`);
  } else if (bhavaBalaRupas < 6.0) {
    score -= 10;
    factors.push(`Deficit Bhava Bala (${bhavaBalaRupas.toFixed(2)} Rupas)`);
  }

  // 6. Layer 6 SAV Modulation
  if (savPoints >= 30) {
    score += 10;
  } else if (savPoints < 24) {
    score -= 10;
  }

  score = Math.max(0, Math.min(100, score));

  // Determine structural contradiction (F-L8-RA-04: Conjunctive evaluation)
  const isLordSeverelyAfflicted = lordDignity === 'Debilitated' && (isDusthana || lordShadbalaRatio < 0.85);
  const isSevereStructuralDeficit = score < 25 && (lordDignity === 'Debilitated' || (isDusthana && lordShadbalaRatio < 0.85));

  let promiseStrength: 'STRONG' | 'MODERATE' | 'WEAK' | 'CONTRADICTED' | 'QUIESCENT' = 'QUIESCENT';
  if (isLordSeverelyAfflicted || isSevereStructuralDeficit) {
    promiseStrength = 'CONTRADICTED';
  } else if (score >= 55) {
    promiseStrength = 'STRONG';
  } else if (score >= 35) {
    promiseStrength = 'MODERATE';
  } else if (score >= 20) {
    promiseStrength = 'WEAK';
  } else {
    promiseStrength = 'QUIESCENT';
  }

  // F-L8-RA-04: Natal promise requires conjunctive evidence; disjunctive short-circuiting purged
  const hasNatalPromise = promiseStrength === 'STRONG' || promiseStrength === 'MODERATE';

  const summaryEn = promiseStrength === 'STRONG'
    ? `Strong natal foundation anchored by dignified ${hLord} and favorable house dynamics.`
    : promiseStrength === 'MODERATE'
    ? `Stable natal potential anchored by ${occupants.length > 0 ? occupants.join(', ') : `${hLord} lordship`}.`
    : promiseStrength === 'CONTRADICTED'
    ? `Afflicted natal foundation due to ${hLord} debility or structural deficit.`
    : `Neutral, quiescent natal potential governed by ${hLord}.`;

  const summaryHi = promiseStrength === 'STRONG'
    ? `${hLord} की सुदृढ़ स्थिति एवं शुभ भाव कारकों द्वारा संरक्षित प्रबल जन्म-संभावना।`
    : promiseStrength === 'MODERATE'
    ? `${occupants.length > 0 ? occupants.join(', ') : `${hLord} भावेश`} द्वारा संरक्षित स्थायी जन्म-संभावना।`
    : promiseStrength === 'CONTRADICTED'
    ? `भावेश ${hLord} की निर्बलता अथवा न्यून बल के कारण संवेदी जन्म-स्थिति।`
    : `${hLord} द्वारा शासित सामान्य, शांत आधार।`;

  return {
    hasNatalPromise,
    promiseStrength,
    score,
    factors,
    factorsHi,
    summaryEn,
    summaryHi,
    lordShadbalaRatio,
    lordShadbalaVirupas,
    bhavaBalaRupas,
    bhavaBalaRank
  };
}

export function evaluateDashaGocharSynthesis(
  kundali: CompleteKundali,
  lang: Language = 'en',
  options: SynthesisEvaluationOptions = {}
): DashaGocharSynthesisResult {
  const isHi = lang === 'hi';
  const lens: DashaGocharLens = options.lens || 'GOCHAR_OVER_DASHA';
  const isLifetime = !!options.isLifetimeMode;

  // 1. Resolve Effective Transit Date (Deterministic priority: transitDate > analysisDateTime > birthDate > fixed epoch)
  let effectiveTransitDate: Date;
  if (options.transitDate) {
    effectiveTransitDate = new Date(options.transitDate);
  } else if (options.analysisDateTime) {
    effectiveTransitDate = new Date(options.analysisDateTime);
  } else if (kundali.birthDetails?.date) {
    effectiveTransitDate = new Date(kundali.birthDetails.date + 'T12:00:00Z');
  } else {
    effectiveTransitDate = new Date('2026-10-07T00:00:00Z');
  }
  const transitDateIso = effectiveTransitDate.toISOString().split('T')[0];

  // 1b. Layer 7 Certified Shadbala & Bhava Bala Integration (Fail-closed or authoritative completeKundali)
  let shadbalaData = kundali.shadbala;
  if (!shadbalaData && kundali.planets && kundali.houses && kundali.birthDetails) {
    const bDate = kundali.birthDetails.date
      ? new Date(kundali.birthDetails.date + 'T' + (kundali.birthDetails.time || '12:00:00'))
      : new Date('2000-01-01T12:00:00Z');
    try {
      shadbalaData = calculateShadbala(kundali.planets, kundali.houses, {
        birthDate: bDate,
        latitude: kundali.birthDetails.latitude,
        longitude: kundali.birthDetails.longitude,
        panchang: kundali.panchang,
        charts: kundali.charts,
        ayanamshaDegrees: (kundali as any).ayanamshaDegrees
      });
    } catch (_err) {
      // Graceful fallback to avoid halting synthesis if coordinates unavailable
    }
  }

  // 2. Resolve Natal Reference Frame (Lagna & Moon)
  const lagnaHouse = kundali.houses?.[0];
  const lagnaRasi = (lagnaHouse?.sign || 'Aries') as ZodiacSign;
  const natalMoon = kundali.planets?.find(p => p.name === 'Moon');
  const moonRasi = (natalMoon?.sign || 'Cancer') as ZodiacSign;

  // 3. Resolve Active Vimshottari Context (Inherited as Source of Truth)
  const dashaList = kundali.dashas || [];
  let isDashaOutOfRange = false;
  let outOfRangeReasonEn: string | undefined = undefined;

  const firstDasha = dashaList[0];
  const lastDasha = dashaList[dashaList.length - 1];
  const firstDashaStartMs = firstDasha ? new Date(firstDasha.startDate).getTime() : NaN;
  const lastDashaEndMs = lastDasha ? new Date(lastDasha.endDate).getTime() : NaN;
  const transitTime = effectiveTransitDate.getTime();

  if (dashaList.length > 0 && !options.selectedMahaPlanet) {
    if (!isNaN(transitTime) && !isNaN(firstDashaStartMs) && transitTime < firstDashaStartMs) {
      isDashaOutOfRange = true;
      outOfRangeReasonEn = `Requested analysis date (${transitDateIso}) is prior to native's birth / earliest Dasha start (${firstDasha.startDate}).`;
    } else if (!isNaN(transitTime) && !isNaN(lastDashaEndMs) && transitTime > lastDashaEndMs) {
      isDashaOutOfRange = true;
      outOfRangeReasonEn = `Requested analysis date (${transitDateIso}) exceeds the supported 120-year Vimshottari lifecycle span (${lastDasha.endDate}).`;
    }
  }

  let mdPlanet: PlanetName = options.selectedMahaPlanet || (dashaList[0]?.planet as PlanetName) || 'Jupiter';
  let adPlanet: PlanetName = options.selectedAntarPlanet || 'Saturn';
  let pdPlanet: PlanetName | undefined = options.selectedPratPlanet;
  let dashaStartDate = options.customStartDate || dashaList[0]?.startDate || '2024-01-01';
  let dashaEndDate = options.customEndDate || dashaList[0]?.endDate || '2027-01-01';

  // If selectedMahaPlanet was passed without custom dates, locate its actual dates
  if (options.selectedMahaPlanet && dashaList.length > 0) {
    const selectedMd = dashaList.find(d => d.planet === options.selectedMahaPlanet);
    if (selectedMd) {
      mdPlanet = selectedMd.planet as PlanetName;
      dashaStartDate = options.customStartDate || selectedMd.startDate;
      dashaEndDate = options.customEndDate || selectedMd.endDate;

      if (options.selectedAntarPlanet && selectedMd.antardashas) {
        const selectedAd = selectedMd.antardashas.find(a => a.planet === options.selectedAntarPlanet);
        if (selectedAd) {
          adPlanet = selectedAd.planet as PlanetName;
          dashaStartDate = options.customStartDate || selectedAd.startDate;
          dashaEndDate = options.customEndDate || selectedAd.endDate;

          if (options.selectedPratPlanet && selectedAd.pratyantardashas) {
            const selectedPd = selectedAd.pratyantardashas.find((p) => p.planet === options.selectedPratPlanet);
            if (selectedPd) {
              pdPlanet = selectedPd.planet as PlanetName;
              dashaStartDate = options.customStartDate || selectedPd.startDate;
              dashaEndDate = options.customEndDate || selectedPd.endDate;
            }
          }
        }
      } else if (selectedMd.antardashas && selectedMd.antardashas.length > 0) {
        const firstAd = selectedMd.antardashas[0];
        adPlanet = firstAd.planet as PlanetName;
        if (!options.customStartDate) dashaStartDate = firstAd.startDate;
        if (!options.customEndDate) dashaEndDate = firstAd.endDate;
      }
    }
  } else if (!options.selectedMahaPlanet && dashaList.length > 0) {
    if (isDashaOutOfRange) {
      // F-L8-RA-06: Explicit OUT_OF_RANGE state without silent fallback
      mdPlanet = 'Sun';
      adPlanet = 'Moon';
      dashaStartDate = transitDateIso;
      dashaEndDate = transitDateIso;
    } else {
      const activeMd = dashaList.find(d => {
        const s = new Date(d.startDate).getTime();
        const e = new Date(d.endDate).getTime();
        return transitTime >= s && transitTime <= e;
      });

      if (activeMd) {
        mdPlanet = activeMd.planet as PlanetName;
        dashaStartDate = activeMd.startDate;
        dashaEndDate = activeMd.endDate;

        if (activeMd.antardashas && activeMd.antardashas.length > 0) {
          const activeAd = activeMd.antardashas.find(a => {
            const s = new Date(a.startDate).getTime();
            const e = new Date(a.endDate).getTime();
            return transitTime >= s && transitTime <= e;
          });

          if (activeAd) {
            adPlanet = activeAd.planet as PlanetName;
            dashaStartDate = activeAd.startDate;
            dashaEndDate = activeAd.endDate;

            const subPratyantars = (activeAd as any).pratyantardashas;
            if (subPratyantars && subPratyantars.length > 0) {
              const activePd = subPratyantars.find((p: any) => {
                const s = new Date(p.startDate).getTime();
                const e = new Date(p.endDate).getTime();
                return transitTime >= s && transitTime <= e;
              });
              if (activePd) {
                pdPlanet = activePd.planet as PlanetName;
                dashaStartDate = activePd.startDate;
                dashaEndDate = activePd.endDate;
              }
            }
          }
        }
      }
    }
  }

  // Natal Dasha Lord placement & owned houses
  const mdNatalPl = kundali.planets?.find(p => p.name === mdPlanet);
  const adNatalPl = kundali.planets?.find(p => p.name === adPlanet);
  const mdHouse = mdNatalPl?.house || 1;
  const adHouse = adNatalPl?.house || 1;

  // Identify houses owned by MD & AD planets
  const mdOwnedHouses: number[] = [];
  const adOwnedHouses: number[] = [];
  (kundali.houses || []).forEach(h => {
    const lord = getSignLord(h.sign as ZodiacSign);
    if (lord === mdPlanet) mdOwnedHouses.push(h.houseNumber);
    if (lord === adPlanet) adOwnedHouses.push(h.houseNumber);
  });

  // Calculate Ascendant longitude and sign boundary distance (F-L8-RA-01)
  const ascPl = kundali.planets?.find(p => p.name === 'Ascendant');
  let ascendantLongitude: number;
  if (ascPl && typeof ascPl.longitude === 'number' && !isNaN(ascPl.longitude)) {
    ascendantLongitude = (ascPl.longitude % 360 + 360) % 360;
  } else if (lagnaHouse?.bhavaMadhya !== undefined && !isNaN(lagnaHouse.bhavaMadhya)) {
    ascendantLongitude = (lagnaHouse.bhavaMadhya % 360 + 360) % 360;
  } else if (lagnaHouse && typeof lagnaHouse.signIndex === 'number') {
    ascendantLongitude = ((lagnaHouse.signIndex * 30 + 15) % 360 + 360) % 360;
  } else {
    ascendantLongitude = 0;
  }

  const ascendantDegreeWithinSign = ascPl?.degreeInSign !== undefined
    ? ascPl.degreeInSign
    : (ascendantLongitude % 30);

  const distanceToPreviousBoundary = ascendantDegreeWithinSign;
  const distanceToNextBoundary = 30 - ascendantDegreeWithinSign;
  const minimumBoundaryDistance = Math.min(distanceToPreviousBoundary, distanceToNextBoundary);

  // Cusp sensitivity boundary is strictly < 2.0 degrees from either boundary
  const isCuspSensitive = minimumBoundaryDistance < 2.0;
  const isBtrPending = kundali.birthDetails?.btrStatus === 'NEEDS_RECALCULATION';
  const isBirthTimeSensitive = isCuspSensitive || isBtrPending;

  const dashaContext: SynthesisDashaContext = {
    level: isDashaOutOfRange ? 'OUT_OF_RANGE' : (pdPlanet ? 'PD' : 'AD'),
    mdPlanet,
    adPlanet,
    pdPlanet,
    startDate: dashaStartDate,
    endDate: dashaEndDate,
    peakDate: null,
    labelEn: isDashaOutOfRange
      ? 'OUT_OF_RANGE'
      : (pdPlanet ? `${mdPlanet} MD – ${adPlanet} AD – ${pdPlanet} PD` : `${mdPlanet} MD – ${adPlanet} AD`),
    labelHi: isDashaOutOfRange
      ? 'दशा चक्र से बाहर'
      : (pdPlanet ? `${mdPlanet} महादशा – ${adPlanet} अंतर्दशा – ${pdPlanet} प्रत्यंतर` : `${mdPlanet} महादशा – ${adPlanet} अंतर्दशा`),
    birthTimeSensitive: isBirthTimeSensitive,
    birthTimeSensitivityReasonEn: isBirthTimeSensitive
      ? (isCuspSensitive
          ? `Birth-time sensitive: Ascendant is at ${ascendantDegreeWithinSign.toFixed(2)}° of sign, within ${minimumBoundaryDistance.toFixed(2)}° of sign cusp boundary (< 2.0° envelope).`
          : 'Birth-time sensitive: Pending birth time rectification status.')
      : `Stable across birth-time tolerances (Ascendant is at ${ascendantDegreeWithinSign.toFixed(2)}° of sign, ${minimumBoundaryDistance.toFixed(2)}° from boundary).`,
    birthTimeSensitivityReasonHi: isBirthTimeSensitive
      ? (isCuspSensitive
          ? `जन्म-समय संवेदनशील: लग्न संधि अंशों के समीप (${ascendantDegreeWithinSign.toFixed(2)}°, संधि से ${minimumBoundaryDistance.toFixed(2)}° दूरी) है।`
          : 'जन्म-समय संवेदनशील: जन्म-समय शोधन लंबित है।')
      : `जन्म-समय सहनशीलता के भीतर स्थिर (लग्न राशि के मध्य में ${ascendantDegreeWithinSign.toFixed(2)}° पर स्थित)।`,
    dashaLordDignityEn: isDashaOutOfRange
      ? 'N/A (Analysis date outside 120-year lifecycle)'
      : (mdNatalPl ? `${mdPlanet} situated in House ${mdHouse} (${mdNatalPl.sign})` : 'Neutral Dignity'),
    dashaLordDignityHi: isDashaOutOfRange
      ? 'लागू नहीं'
      : (mdNatalPl ? `${mdPlanet} का ${mdHouse}वें भाव (${mdNatalPl.sign}) में स्थित होना` : 'सामान्य स्थिति'),
    ownedHouses: isDashaOutOfRange ? [] : Array.from(new Set([...mdOwnedHouses, ...adOwnedHouses])).sort((a, b) => a - b),
    occupiedHouse: isDashaOutOfRange ? 1 : mdHouse,
    ascendantLongitude,
    ascendantDegreeWithinSign,
    minimumBoundaryDistance
  };

  // 4. Resolve Live Verified Gochar Ephemeris
  const convention = kundali.nodeConvention || kundali.birthDetails?.nodeConvention || 'mean';
  const liveEphemArray = calculateLivePlanetaryEphemeris(effectiveTransitDate, convention);
  const liveEphemLookup: Record<string, (typeof liveEphemArray)[0]> = {};
  liveEphemArray.forEach(item => {
    liveEphemLookup[item.planet] = item;
  });
  const transitPlanetsMap: Record<PlanetName, TransitPlanetImpact> = {} as any;
  const validPlanets: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];
  const prastaraTables = kundali.planets && kundali.planets.length > 0 ? calculatePrastaraTables(kundali) : undefined;

  validPlanets.forEach(pName => {
    const live = liveEphemLookup[pName];
    if (live) {
      const transitSign = live.sign as ZodiacSign;
      const tHouseLagna = calculateSignDistance(lagnaRasi, transitSign);
      const tHouseMoon = calculateSignDistance(moonRasi, transitSign);
      const drishtiHouses = getSpecialDrishtiHouses(pName, tHouseLagna);

      // Check contacts with natal planets
      const natalContacts: TransitPlanetImpact['natalContacts'] = [];
      (kundali.planets || []).forEach(np => {
        if (np.name !== 'Ascendant') {
          const npSign = np.sign as ZodiacSign;
          const dist = calculateSignDistance(npSign, transitSign);
          const proximity = Math.abs(live.degreeInSign - (np.degreeInSign || 0));

          if (dist === 1) {
            natalContacts.push({
              natalPlanet: np.name as PlanetName,
              natalHouse: np.house,
              proximityDeg: Number(proximity.toFixed(1)),
              nature: 'YUTI'
            });
          } else if (dist === 7) {
            natalContacts.push({
              natalPlanet: np.name as PlanetName,
              natalHouse: np.house,
              proximityDeg: Number(proximity.toFixed(1)),
              nature: 'SAMASAPTAKA'
            });
          } else if (dist === 5 || dist === 9) {
            if (proximity <= 6.0) {
              natalContacts.push({
                natalPlanet: np.name as PlanetName,
                natalHouse: np.house,
                proximityDeg: Number(proximity.toFixed(1)),
                nature: 'TRIKONA_SAMBANDHA'
              });
            }
          } else if (dist === 4 || dist === 10) {
            if (proximity <= 5.0) {
              natalContacts.push({
                natalPlanet: np.name as PlanetName,
                natalHouse: np.house,
                proximityDeg: Number(proximity.toFixed(1)),
                nature: 'KENDRA_SAMBANDHA'
              });
            }
          }
        }
      });

      // Assess transit timing phase
      let transitPhase: TransitPlanetImpact['transitPhase'] = 'APPROACH';
      let transitPhaseLabelEn = 'Approaching Active Zone';
      let transitPhaseLabelHi = 'सक्रिय क्षेत्र की ओर अग्रसर';

      if (live.isRetrograde) {
        transitPhase = 'RETROGRADE_STATION';
        transitPhaseLabelEn = 'Retrograde / Karmic Re-evaluation';
        transitPhaseLabelHi = 'वक्री गति / कर्म समीक्षा';
      } else if (live.degreeInSign < 3.0) {
        transitPhase = 'INGRESS';
        transitPhaseLabelEn = 'Sign Ingress / Entry Phase';
        transitPhaseLabelHi = 'राशि प्रवेश काल';
      } else if (live.degreeInSign > 27.0) {
        transitPhase = 'DEPARTURE';
        transitPhaseLabelEn = 'Departure / Transition Phase';
        transitPhaseLabelHi = 'राशि निकास काल';
      } else {
        const closestContact = natalContacts.length > 0
          ? natalContacts.reduce((min, c) => c.proximityDeg < min ? c.proximityDeg : min, 999)
          : 999;
        if (closestContact <= 1.2) {
          transitPhase = 'EXACT_CONTACT';
          transitPhaseLabelEn = 'Exact Harmonic Contact (Peak Trigger)';
          transitPhaseLabelHi = 'सटीक ग्रह स्पर्श (चरम सक्रियता)';
        } else if (closestContact <= 4.5) {
          transitPhase = 'APPROACH';
          transitPhaseLabelEn = 'Approaching Peak Contact';
          transitPhaseLabelHi = 'चरम स्पर्श की ओर अग्रसर';
        } else {
          transitPhase = 'SEPARATION';
          transitPhaseLabelEn = 'Separating / Integration Phase';
          transitPhaseLabelHi = 'वियोजन काल';
        }
      }

      // Assess relevance to active Dasha lords
      let dashaRelevanceEn = 'General background transit';
      let dashaRelevanceHi = 'सामान्य गोचर प्रभाव';
      if (pName === mdPlanet) {
        dashaRelevanceEn = `Primary Dasha Lord (${mdPlanet}) transiting natal House ${tHouseLagna}!`;
        dashaRelevanceHi = `महादशा स्वामी (${mdPlanet}) का लग्न से ${tHouseLagna}वें भाव में गोचर!`;
      } else if (pName === adPlanet) {
        dashaRelevanceEn = `Antardasha Lord (${adPlanet}) actively transiting House ${tHouseLagna}!`;
        dashaRelevanceHi = `अंतर्दशा स्वामी (${adPlanet}) का ${tHouseLagna}वें भाव में सक्रिय गोचर!`;
      } else if (['Jupiter', 'Saturn'].includes(pName)) {
        dashaRelevanceEn = `Major cosmic time-keeper (${pName}) transiting House ${tHouseLagna}.`;
        dashaRelevanceHi = `प्रमुख काल-निर्धारक ग्रह (${pName}) का ${tHouseLagna}वें भाव में गोचर।`;
      }

      const nakIdx = Math.floor(live.longitude / (360 / 27));
      const nakObj = NAKSHATRAS[nakIdx] || { name: 'Ashwini', lord: 'Ketu' };
      const degInNak = live.longitude % (360 / 27);
      const pada = Math.floor(degInNak / (360 / 108)) + 1;

      const kakshya = calculateKakshyaTransitInfo(
        pName,
        live.longitude,
        prastaraTables?.[pName]?.contributors
      );

      transitPlanetsMap[pName] = {
        planet: pName,
        sign: transitSign,
        degree: live.degreeInSign,
        degreeFormatted: `${live.degreeInSign.toFixed(1)}°`,
        nakshatra: nakObj.name,
        pada: pada,
        transitHouseFromLagna: tHouseLagna,
        transitHouseFromMoon: tHouseMoon,
        isRetrograde: !!live.isRetrograde,
        drishtiHouses,
        natalContacts,
        transitPhase,
        transitPhaseLabelEn,
        transitPhaseLabelHi,
        dashaRelevanceEn,
        dashaRelevanceHi,
        kakshyaInfo: kakshya
      };
    }
  });

  const gocharContext: SynthesisGocharContext = {
    dateIso: transitDateIso,
    dateFormatted: effectiveTransitDate.toLocaleDateString(isHi ? 'hi-IN' : 'en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }),
    timeFormatted: effectiveTransitDate.toLocaleTimeString(isHi ? 'hi-IN' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit'
    }),
    planets: transitPlanetsMap,
    majorTransitsOnly: false
  };

  // 5. Evaluate House Activation Model for all 12 Houses
  const houseActivations: Record<number, HouseActivationEvidence> = {};
  const topStimulatedHouses: number[] = [];
  let strongCorroborationCount = 0;
  let moderateCorroborationCount = 0;
  let natalPlusDashaCount = 0;
  const contradictionFlags: DashaGocharSynthesisResult['contradictionFlags'] = [];

  for (let h = 1; h <= 12; h++) {
    const houseObj = kundali.houses?.[h - 1];
    const hSign = (houseObj?.sign || ZODIAC_SIGNS[h - 1]) as ZodiacSign;
    const hLord = getSignLord(hSign);
    const occupants = (kundali.planets || []).filter(p => p.house === h).map(p => p.name as PlanetName);
    const aspects = (kundali.planets || [])
      .filter(p => p.name !== 'Ascendant' && getSpecialDrishtiHouses(p.name as PlanetName, p.house).includes(h))
      .map(p => p.name as PlanetName);

    // Factor A: Natal Promise (Evaluated via Parashari Dignity, Placement, Bala & SAV)
    const savPoints = kundali.ashtakvarga ? getSavByBhava(kundali.ashtakvarga, h, kundali.houses) : 28;
    const promiseEvaluation = evaluateNatalPromiseForHouse(
      h,
      hSign,
      hLord,
      kundali,
      occupants,
      aspects,
      savPoints,
      shadbalaData
    );

    const hasNatalPromise = promiseEvaluation.hasNatalPromise;
    const natalFactors = promiseEvaluation.factors;
    const natalPromiseSummaryEn = promiseEvaluation.summaryEn;
    const natalPromiseSummaryHi = promiseEvaluation.summaryHi;
    const natalPromiseEvaluation = {
      hasNatalPromise: promiseEvaluation.hasNatalPromise,
      promiseStrength: promiseEvaluation.promiseStrength,
      score: promiseEvaluation.score,
      factors: promiseEvaluation.factors,
      factorsHi: promiseEvaluation.factorsHi
    };

    // Factor B: Dasha Involvement
    const isMdLord = hLord === mdPlanet;
    const isAdLord = hLord === adPlanet;
    const isMdOccupant = occupants.includes(mdPlanet);
    const isAdOccupant = occupants.includes(adPlanet);
    const isMdAspecting = aspects.includes(mdPlanet);
    const isAdAspecting = aspects.includes(adPlanet);
    const isPdLord = pdPlanet ? hLord === pdPlanet : false;
    const isPdOccupant = pdPlanet ? occupants.includes(pdPlanet) : false;
    const isPdAspecting = pdPlanet ? aspects.includes(pdPlanet) : false;

    const isDashaActive = isMdLord || isAdLord || isMdOccupant || isAdOccupant || isMdAspecting || isAdAspecting || isPdLord || isPdOccupant || isPdAspecting;
    let dashaInvolvementRole: HouseActivationEvidence['dashaInvolvementRole'] = 'NONE';
    const dashaPlanetsInvolved: PlanetName[] = [];

    if (isMdLord || isMdOccupant || isMdAspecting) {
      dashaPlanetsInvolved.push(mdPlanet);
      dashaInvolvementRole = 'MD';
    }
    if (isAdLord || isAdOccupant || isAdAspecting) {
      dashaPlanetsInvolved.push(adPlanet);
      dashaInvolvementRole = dashaInvolvementRole === 'MD' ? 'MULTIPLE' : 'AD';
    }
    if (pdPlanet && (isPdLord || isPdOccupant || isPdAspecting)) {
      dashaPlanetsInvolved.push(pdPlanet);
      if (dashaInvolvementRole === 'NONE') dashaInvolvementRole = 'PD';
      else dashaInvolvementRole = 'MULTIPLE';
    }

    const dashaSummaryEn = isDashaActive
      ? `Active Dasha stimulus via ${dashaPlanetsInvolved.join(' & ')} (${isMdLord || isAdLord ? 'Lordship' : 'Occupation/Aspect'}).`
      : 'Quiescent in the current primary Dasha cycle.';
    const dashaSummaryHi = isDashaActive
      ? `${dashaPlanetsInvolved.join(' व ')} द्वारा सक्रिय दशा प्रेरणा (${isMdLord || isAdLord ? 'भावेश' : 'स्थिति/दृष्टि'})।`
      : 'वर्तमान मुख्य दशा चक्र में शांत।';

    // Factor C: Gochar / Transit Trigger
    const transitOccupants: PlanetName[] = [];
    const transitAspects: PlanetName[] = [];

    validPlanets.forEach(p => {
      const t = transitPlanetsMap[p];
      if (t) {
        if (t.transitHouseFromLagna === h) transitOccupants.push(p);
        if (t.drishtiHouses.includes(h)) transitAspects.push(p);
      }
    });

    const isGocharActive = transitOccupants.length > 0 || transitAspects.includes('Jupiter') || transitAspects.includes('Saturn') || transitAspects.includes(mdPlanet) || transitAspects.includes(adPlanet);
    const gocharSummaryEn = isGocharActive
      ? `Transit stimulation via ${[...transitOccupants, ...transitAspects.map(p => `${p} Drishti`)].join(', ')}.`
      : 'No primary transit stimulus in current window.';
    const gocharSummaryHi = isGocharActive
      ? `${[...transitOccupants, ...transitAspects.map(p => `${p} दृष्टि`)].join(', ')} द्वारा तात्कालिक गोचर प्रभाव।`
      : 'वर्तमान काल में कोई मुख्य गोचर सक्रिय नहीं।';

    // Factor C2: Ashtakavarga Evidence Bridge
    const isSAVStrong = savPoints >= 28;
    const classicalPlanets: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
    const bavPoints: Partial<Record<PlanetName, number>> = {};
    if (kundali.ashtakvarga) {
      classicalPlanets.forEach(p => {
        bavPoints[p] = getBavByBhava(kundali.ashtakvarga, p, h, kundali.houses);
      });
    }

    const ashtakavargaEvidence = {
      savPoints,
      isSAVStrong,
      bavPoints,
      statusEn: savPoints >= 30
        ? `Abundant fortification (${savPoints} bindus ≥ 30)`
        : savPoints >= 28
        ? `Adequate classical support (${savPoints} bindus)`
        : `Deficient capacity (${savPoints} bindus < 28)`,
      statusHi: savPoints >= 30
        ? `प्रचुर शुभ संबल (${savPoints} बिंदु ≥ 30)`
        : savPoints >= 28
        ? `संतुलित शास्त्रीय आधार (${savPoints} बिंदु)`
        : `संवेदनशील क्षेत्र (${savPoints} बिंदु < 28)`
    };

    // Factor C3: Layer 7 Certified Shadbala & Bhava Bala Evidence Bridge
    const bhavaBalaRupas = promiseEvaluation.bhavaBalaRupas;
    const bhavaBalaRank = promiseEvaluation.bhavaBalaRank;
    const lordShadbalaRatio = promiseEvaluation.lordShadbalaRatio;
    const lordShadbalaVirupas = promiseEvaluation.lordShadbalaVirupas;
    const isBhavaStrong = bhavaBalaRupas >= 7.5;
    const isLordStrong = lordShadbalaRatio >= 1.0;

    const mdLordItem = shadbalaData?.planets?.find((p: any) => p.planet === mdPlanet);
    const mdLordShadbalaRatio = mdLordItem?.ratio;

    const shadbalaBhavaBalaEvidence = {
      bhavaBalaRupas,
      bhavaBalaRank,
      isBhavaStrong,
      lordShadbalaVirupas,
      lordShadbalaRatio,
      isLordStrong,
      mdLordShadbalaRatio,
      statusEn: isBhavaStrong && isLordStrong
        ? `Fortified: High Bhava Bala (${bhavaBalaRupas.toFixed(2)} Rupas) & Lord Shadbala (${lordShadbalaRatio.toFixed(2)} ratio)`
        : isLordStrong
        ? `Capable Lord: Shadbala (${lordShadbalaRatio.toFixed(2)} ratio)`
        : isBhavaStrong
        ? `Supported House: Bhava Bala (${bhavaBalaRupas.toFixed(2)} Rupas)`
        : `Deficit Strength: Subdued Bhava Bala (${bhavaBalaRupas.toFixed(2)} Rupas) & Lord (${lordShadbalaRatio.toFixed(2)} ratio)`,
      statusHi: isBhavaStrong && isLordStrong
        ? `सुदृढ़: उच्च भाव बल (${bhavaBalaRupas.toFixed(2)} रूप) एवं भावेश षड्बल (${lordShadbalaRatio.toFixed(2)} अनुपात)`
        : isLordStrong
        ? `सक्षम भावेश: षड्बल (${lordShadbalaRatio.toFixed(2)} अनुपात)`
        : isBhavaStrong
        ? `सबल भाव: भाव बल (${bhavaBalaRupas.toFixed(2)} रूप)`
        : `संवेदनशील: न्यून भाव बल (${bhavaBalaRupas.toFixed(2)} रूप) व भावेश बल (${lordShadbalaRatio.toFixed(2)})`
    };

    // Factor D: Corroboration Synthesis & Parashari Hierarchy
    let corroborationLevel: CorroborationLevel = 'INACTIVE';
    let corroborationBadgeEn = 'Inactive';
    let corroborationBadgeHi = 'अक्रिय';
    const supportingMechanismsEn: string[] = [];
    const supportingMechanismsHi: string[] = [];
    const contradictionsEn: string[] = [];
    const contradictionsHi: string[] = [];

    // Layer 6 & Layer 7 Fortifications
    if (savPoints >= 30 && (isDashaActive || isGocharActive)) {
      supportingMechanismsEn.push(`Strong Sarvashtakavarga strength (${savPoints} bindus ≥ 30) shields and augments house materialization.`);
      supportingMechanismsHi.push(`सुदृढ़ सर्वाष्टकवर्ग बल (${savPoints} बिंदु ≥ 30) भाव के शुभ परिणामों में वृद्धि करता है।`);
    } else if (savPoints < 25 && isGocharActive) {
      contradictionsEn.push(`Low Sarvashtakavarga strength (${savPoints} bindus < 25) introduces structural vulnerability under transit stress.`);
      contradictionsHi.push(`न्यून सर्वाष्टकवर्ग बल (${savPoints} बिंदु < 25) गोचर के दबाव में संवेदनशीलता उत्पन्न करता है।`);
    }

    if (isBhavaStrong && (isDashaActive || isGocharActive)) {
      supportingMechanismsEn.push(`Certified Bhava Bala (${bhavaBalaRupas.toFixed(2)} Rupas) reinforces house manifestation capacity.`);
      supportingMechanismsHi.push(`प्रमाणित भाव बल (${bhavaBalaRupas.toFixed(2)} रूप) भाव की फल-प्रदान क्षमता को पुष्ट करता है।`);
    } else if (bhavaBalaRupas < 6.5 && isDashaActive) {
      contradictionsEn.push(`Subdued Bhava Bala (${bhavaBalaRupas.toFixed(2)} Rupas < 6.5) signals structural friction or effort required.`);
      contradictionsHi.push(`न्यून भाव बल (${bhavaBalaRupas.toFixed(2)} रूप < 6.5) भाव फल-प्राप्ति में अधिक परिश्रम का संकेत देता है।`);
    }

    if (lordShadbalaRatio >= 1.15 && isDashaActive) {
      supportingMechanismsEn.push(`House lord ${hLord} is strongly fortified in Shadbala (${lordShadbalaRatio.toFixed(2)} ratio ≥ 1.0).`);
      supportingMechanismsHi.push(`भावेश ${hLord} षड्बल में पूर्ण सक्षम (${lordShadbalaRatio.toFixed(2)} अनुपात ≥ 1.0) है।`);
    } else if (lordShadbalaRatio < 0.85 && isDashaActive) {
      contradictionsEn.push(`House lord ${hLord} suffers deficit Shadbala (${lordShadbalaRatio.toFixed(2)} ratio < 1.0), indicating delayed materialization.`);
      contradictionsHi.push(`भावेश ${hLord} षड्बल में न्यून (${lordShadbalaRatio.toFixed(2)} अनुपात < 1.0) है, जिससे फल में विलंब संभव है।`);
    }

    // Parashari Hierarchy Enforcement
    if (hasNatalPromise && isDashaActive && isGocharActive) {
      corroborationLevel = 'STRONG_CORROBORATION';
      corroborationBadgeEn = 'Strong Corroboration (Natal + Dasha + Gochar)';
      corroborationBadgeHi = 'प्रबल संपुष्टि (जन्म + दशा + गोचर)';
      strongCorroborationCount++;
      topStimulatedHouses.push(h);

      supportingMechanismsEn.push(`Natal foundation present in ${hSign}`);
      supportingMechanismsEn.push(`Activated by ruling Dasha (${dashaPlanetsInvolved.join(', ')})`);
      supportingMechanismsEn.push(`Directly triggered by transit (${transitOccupants.concat(transitAspects).join(', ')})`);

      supportingMechanismsHi.push(`${hSign} राशि में जन्मकालीन आधार उपस्थित`);
      supportingMechanismsHi.push(`सक्रिय दशा स्वामी (${dashaPlanetsInvolved.join(', ')}) द्वारा जाग्रत`);
      supportingMechanismsHi.push(`गोचर ग्रह (${transitOccupants.concat(transitAspects).join(', ')}) द्वारा तात्कालिक प्रेरणा`);
    } else if (hasNatalPromise && isDashaActive && !isGocharActive) {
      // F-L8-03 remediation: Preserves Dasha activation during active Dasha window
      corroborationLevel = 'NATAL_PLUS_DASHA_ACTIVATION';
      corroborationBadgeEn = 'Natal Promise + Active Dasha (Awaiting Transit Trigger)';
      corroborationBadgeHi = 'सक्रिय दशा संपुष्टि (गोचर-प्रेरणा प्रतीक्षारत)';
      natalPlusDashaCount++;
      topStimulatedHouses.push(h);

      supportingMechanismsEn.push(`Natal foundation in ${hSign} actively energized by ruling Dasha (${dashaPlanetsInvolved.join(', ')})`);
      supportingMechanismsEn.push(`Operating within developmental dasha corridor awaiting acute transit trigger`);

      supportingMechanismsHi.push(`${hSign} राशि में जन्मकालीन आधार सक्रिय दशा स्वामी (${dashaPlanetsInvolved.join(', ')}) द्वारा जाग्रत`);
      supportingMechanismsHi.push(`तात्कालिक गोचरीय उत्प्रेरण की प्रतीक्षा में विकासात्मक दशा काल`);
    } else if (isDashaActive && !hasNatalPromise) {
      corroborationLevel = 'DASHA_WITHOUT_SUFFICIENT_NATAL_PROMISE';
      corroborationBadgeEn = 'Dasha Stimulation (Unanchored / Deficit Natal Foundation)';
      corroborationBadgeHi = 'दशा सक्रियता (न्यून जन्मकालीन आधार)';

      supportingMechanismsEn.push(`Active Dasha (${dashaPlanetsInvolved.join(', ')}) stimulates House ${h}, but weak natal promise limits material fruition`);
      supportingMechanismsHi.push(`सक्रिय दशा (${dashaPlanetsInvolved.join(', ')}) ${h}वें भाव को उत्तेजित करती है, किंतु न्यून जन्म संस्कार फल-प्राप्ति को सीमित करते हैं`);
    } else if (hasNatalPromise && isGocharActive) {
      corroborationLevel = 'MODERATE_CORROBORATION';
      corroborationBadgeEn = 'Moderate Corroboration (Natal Promise + Gochar Trigger)';
      corroborationBadgeHi = 'मध्यम संपुष्टि (जन्म संभावना + गोचर)';
      moderateCorroborationCount++;
      topStimulatedHouses.push(h);

      supportingMechanismsEn.push(`Natal foundation in ${hSign} receives passing transit trigger without active Dasha gate`);
      supportingMechanismsHi.push(`${hSign} राशि में जन्मकालीन आधार पर तात्कालिक गोचर प्रभाव (दशा समर्थन के बिना)`);
    } else if (isGocharActive) {
      corroborationLevel = 'SINGLE_SOURCE_GOCHAR';
      corroborationBadgeEn = 'Single-Source Transit (Gochar Only)';
      corroborationBadgeHi = 'केवल गोचर-प्रेरित (अस्थायी)';
      supportingMechanismsEn.push(`Transient planetary pass without deep Dasha anchor or natal foundation`);
      supportingMechanismsHi.push(`दशा एवं जन्म आधार के बिना केवल तात्कालिक गोचरीय संचलन`);
    } else if (hasNatalPromise) {
      corroborationLevel = 'NATAL_POTENTIAL_ONLY';
      corroborationBadgeEn = 'Natal Potential (Quiescent Timing)';
      corroborationBadgeHi = 'जन्म-संभावना (प्रतीक्षारत काल)';
      supportingMechanismsEn.push(`Latent promise awaiting future Dasha window`);
      supportingMechanismsHi.push(`सुप्त संभावना जो भावी अनुकूल दशा की प्रतीक्षा में है`);
    } else {
      corroborationLevel = 'INACTIVE';
      corroborationBadgeEn = 'Inactive (Quiescent)';
      corroborationBadgeHi = 'अक्रिय (शांत)';
    }

    // Contradiction Detection
    const hLordPl = kundali.planets?.find(p => p.name === hLord);
    if (isDashaActive && isGocharActive && hLordPl?.dignity === 'Debilitated') {
      contradictionsEn.push(`Natal lord ${hLord} is debilitated, introducing friction or delayed fruit despite transit activity.`);
      contradictionsHi.push(`भावेश ${hLord} जन्म कुंडली में नीचस्थ है, जिससे गोचर सक्रियता के बावजूद विलंब या संघर्ष संभव है।`);
      contradictionFlags.push({
        houseNumber: h,
        issueEn: `House ${h} has active Dasha-Gochar stimulus, but its lord ${hLord} suffers natal debility.`,
        issueHi: `${h}वें भाव में दशा-गोचर सक्रिय हैं किंतु भावेश ${hLord} जन्म कुंडली में निर्बल है।`,
        resolutionEn: 'Manifests as substantial effort requiring disciplined perseverance rather than effortless fortune.',
        resolutionHi: 'सरल सफलता के स्थान पर कठिन परिश्रम एवं धैर्यपूर्वक प्रयास से फल प्राप्ति होगी।'
      });
    }

    // Check if Saturn transit aspects house during Jupiter Dasha
    if (transitAspects.includes('Saturn') && dashaPlanetsInvolved.includes('Jupiter')) {
      contradictionsEn.push(`Saturn's restrictive transit Drishti tempers Jupiter's expansive Dasha promise.`);
      contradictionsHi.push(`शनि की गोचरीय दृष्टि बृहस्पति की विस्तारवादी दशा में संयम एवं नियमों की मांग करती है।`);
    }

    // Timing calculation (F-L8-RA-02: Zero midpoint fabrication, honest peak and uncertainty modeling)
    let peakDate: string | null = null;
    let timingState: TimingState = 'NO_DEFENSIBLE_PEAK';
    let timingReason = 'INSUFFICIENT_TIMING_CONVERGENCE';
    let uncertaintyDays: number | undefined = undefined;

    const sMs = new Date(dashaStartDate).getTime();
    const eMs = new Date(dashaEndDate).getTime();
    const tMs = effectiveTransitDate.getTime();
    const isTransitInWindow = !isNaN(tMs) && !isNaN(sMs) && !isNaN(eMs) && tMs >= sMs && tMs <= eMs;

    if (!isDashaOutOfRange && (corroborationLevel === 'STRONG_CORROBORATION' || (isGocharActive && isDashaActive && hasNatalPromise))) {
      // Genuine astronomical convergence
      if (transitOccupants.length > 0 && isTransitInWindow) {
        const primaryTransitPl = transitOccupants[0];
        const tImpact = transitPlanetsMap[primaryTransitPl];
        if (tImpact?.transitPhase === 'EXACT_CONTACT') {
          peakDate = transitDateIso;
          timingState = 'TIMING_CONFIRMED';
          timingReason = `Exact astronomical contact (${primaryTransitPl} in House ${h} at ${tImpact.degree.toFixed(2)}°) during active Dasha window.`;
          uncertaintyDays = 3;
        } else if (tImpact?.transitPhase === 'APPROACH' || tImpact?.transitPhase === 'INGRESS') {
          peakDate = transitDateIso;
          timingState = 'TIMING_APPROXIMATE';
          timingReason = `Astronomical ingress/approach (${primaryTransitPl} in House ${h}) during active Dasha window.`;
          uncertaintyDays = 14;
        } else {
          peakDate = transitDateIso;
          timingState = 'TIMING_APPROXIMATE';
          timingReason = `Transit occupation (${primaryTransitPl} in House ${h}) during active Dasha corridor.`;
          uncertaintyDays = 21;
        }
      } else if (transitAspects.length > 0 && isTransitInWindow) {
        const aspectingPl = transitAspects[0];
        const tImpact = transitPlanetsMap[aspectingPl];
        if (tImpact?.transitPhase === 'EXACT_CONTACT') {
          peakDate = transitDateIso;
          timingState = 'TIMING_CONFIRMED';
          timingReason = `Exact transit Drishti from ${aspectingPl} upon House ${h} during active Dasha.`;
          uncertaintyDays = 5;
        } else {
          peakDate = transitDateIso;
          timingState = 'TIMING_APPROXIMATE';
          timingReason = `Approaching transit Drishti from ${aspectingPl} upon House ${h} during active Dasha.`;
          uncertaintyDays = 30;
        }
      } else {
        // Active Dasha and Natal Promise, but transit is not in exact contact window
        peakDate = null;
        timingState = 'TIMING_WINDOW_ONLY';
        timingReason = 'Active Dasha developmental window awaiting acute Gochar trigger.';
        uncertaintyDays = 45;
      }
    } else if (!isDashaOutOfRange && isDashaActive && hasNatalPromise) {
      // Developmental corridor awaiting Gochar
      peakDate = null;
      timingState = 'TIMING_WINDOW_ONLY';
      timingReason = 'Active Dasha window upon natal promise; awaiting acute Gochar trigger.';
      uncertaintyDays = 60;
    } else if (isGocharActive && !isDashaActive) {
      // Transient passing Gochar without Dasha sanction
      peakDate = null;
      timingState = 'TIMING_UNCERTAIN';
      timingReason = 'Passing transit without Dasha sanction cannot establish a durable event peak.';
      uncertaintyDays = 90;
    } else {
      // Inactive or quiescent natal potential
      peakDate = null;
      timingState = 'NO_DEFENSIBLE_PEAK';
      timingReason = 'No active Dasha-Gochar convergence to justify an event peak date.';
      uncertaintyDays = undefined;
    }

    const timing = {
      startDate: dashaStartDate,
      endDate: dashaEndDate,
      peakDate,
      uncertaintyDays,
      timingState,
      timingReason,
      timeframeDescriptionEn: peakDate
        ? `Peak activation at ${peakDate} (${timingState}) within ${dashaContext.labelEn} window (${dashaStartDate} → ${dashaEndDate}).`
        : `Operating within ${dashaContext.labelEn} window (${dashaStartDate} → ${dashaEndDate}); peak date null (${timingState}).`,
      timeframeDescriptionHi: peakDate
        ? `${peakDate} को मुख्य सक्रियता (${dashaStartDate} से ${dashaEndDate} कालखंड)।`
        : `${dashaStartDate} से ${dashaEndDate} कालखंड के भीतर; निश्चित चरमोत्कर्ष तिथि अनुपलब्ध।`
    };

    const confidence: ConfidenceRating = contradictionsEn.length > 1
      ? 'REQUIRES_ACHARYA_PAUSE'
      : (corroborationLevel === 'STRONG_CORROBORATION' && contradictionsEn.length === 0 && isLordStrong)
      ? 'HIGH'
      : (corroborationLevel === 'STRONG_CORROBORATION' || corroborationLevel === 'NATAL_PLUS_DASHA_ACTIVATION' || (corroborationLevel === 'MODERATE_CORROBORATION' && isLordStrong))
      ? 'MODERATE'
      : 'LOW';

    // Formulate Interpretations
    const simpleInterpretationEn = corroborationLevel === 'STRONG_CORROBORATION'
      ? `Themes of House ${h} (${hSign}) are strongly energized right now through convergent Dasha and planetary transits.`
      : corroborationLevel === 'NATAL_PLUS_DASHA_ACTIVATION'
      ? `House ${h} (${hSign}) is actively energized by the current Dasha cycle (${dashaPlanetsInvolved.join(', ')}), unfolding structural growth while awaiting acute transit triggers.`
      : corroborationLevel === 'MODERATE_CORROBORATION'
      ? `Noticeable activity and opportunities relating to House ${h} arise during this current period.`
      : corroborationLevel === 'DASHA_WITHOUT_SUFFICIENT_NATAL_PROMISE'
      ? `Dasha influence brings focus to House ${h}, but modest natal promise yields internal reorientation rather than major external shifts.`
      : corroborationLevel === 'SINGLE_SOURCE_GOCHAR'
      ? `Passing events or minor interactions touch House ${h} without altering permanent life foundations.`
      : `House ${h} remains in a steady, latent state.`;

    const simpleInterpretationHi = corroborationLevel === 'STRONG_CORROBORATION'
      ? `${h}वें भाव (${hSign}) के विषय वर्तमान में दशा एवं गोचर के प्रबल संमिलन द्वारा अत्यधिक सक्रिय हैं।`
      : corroborationLevel === 'NATAL_PLUS_DASHA_ACTIVATION'
      ? `${h}वें भाव (${hSign}) में सक्रिय दशा स्वामी (${dashaPlanetsInvolved.join(', ')}) द्वारा विकासात्मक प्रगति जाग्रत है, जो तात्कालिक गोचर प्रेरणा की प्रतीक्षा में है।`
      : corroborationLevel === 'MODERATE_CORROBORATION'
      ? `${h}वें भाव से संबंधित गतिविधियों एवं अवसरों का वर्तमान कालखंड में प्रत्यक्ष अनुभव होगा।`
      : corroborationLevel === 'DASHA_WITHOUT_SUFFICIENT_NATAL_PROMISE'
      ? `दशा प्रभाव ${h}वें भाव पर केंद्रित है, किंतु सामान्य जन्म संस्कार के कारण आंतरिक परिवर्तन अधिक दृष्टिगोचर होंगे।`
      : corroborationLevel === 'SINGLE_SOURCE_GOCHAR'
      ? `${h}वें भाव पर तात्कालिक गोचर का प्रभाव है, जो स्थायी परिवर्तन के स्थान पर अल्पकालिक है।`
      : `${h}वां भाव वर्तमान में संतुलित एवं प्रतीक्षारत अवस्था में है।`;

    const technicalInterpretationEn = `House ${h} (${hSign}): Lord ${hLord} (House ${hLordPl?.house || 1}, Shadbala ratio ${lordShadbalaRatio.toFixed(2)}, Bhava Bala ${bhavaBalaRupas.toFixed(2)} Rupas). ${
      corroborationLevel === 'STRONG_CORROBORATION'
        ? 'Triple convergence (Natal promise + Dasha lordship/aspect + Transit contact). High materialization probability.'
        : corroborationLevel === 'NATAL_PLUS_DASHA_ACTIVATION'
        ? 'Dasha activation upon verified natal promise. Sustained developmental tenure awaiting acute transit trigger.'
        : corroborationLevel === 'MODERATE_CORROBORATION'
        ? 'Dasha-Gochar dual activation. Yields noticeable developments proportional to planetary dignity.'
        : corroborationLevel === 'DASHA_WITHOUT_SUFFICIENT_NATAL_PROMISE'
        ? 'Dasha stimulation without robust natal foundation; produces subjective striving rather than durable objective fruits.'
        : corroborationLevel === 'SINGLE_SOURCE_GOCHAR'
        ? 'Transient transit trigger without Dasha sanction; produces fleeting psychological or environmental impressions.'
        : 'Quiescent bhava in active Vimshottari hierarchy.'
    }`;

    const technicalInterpretationHi = `${h}वां भाव (${hSign}): भावेश ${hLord} (${hLordPl?.house || 1}वें भाव में, षड्बल ${lordShadbalaRatio.toFixed(2)}, भाव बल ${bhavaBalaRupas.toFixed(2)} रूप)। ${
      corroborationLevel === 'STRONG_CORROBORATION'
        ? 'त्रि-आयामी संमिलन (जन्म संस्कार + दशा प्रभुत्व + गोचर स्पर्श)। फल-प्राप्ति की उच्च शास्त्रीय संभावना।'
        : corroborationLevel === 'NATAL_PLUS_DASHA_ACTIVATION'
        ? 'प्रमाणित जन्म संस्कार पर दशा सक्रियता। तात्कालिक गोचर पूर्व सतत विकासात्मक काल।'
        : corroborationLevel === 'MODERATE_CORROBORATION'
        ? 'दशा-गोचर संयुक्त सक्रियता। ग्रह-बल के अनुपात में स्पष्ट परिणाम परिलक्षित होंगे।'
        : corroborationLevel === 'DASHA_WITHOUT_SUFFICIENT_NATAL_PROMISE'
        ? 'न्यून जन्म संस्कार पर दशा प्रेरणा; स्थायी फल के स्थान पर आत्म-मंथन।'
        : corroborationLevel === 'SINGLE_SOURCE_GOCHAR'
        ? 'दशा के समर्थन के बिना केवल गोचरीय प्रभाव; तात्कालिक मनोदशा अथवा अस्थायी घटनाएं।'
        : 'विंशोत्तरी पदानुक्रम में वर्तमान में शांत भाव।'
    }`;

    houseActivations[h] = {
      houseNumber: h,
      sign: hSign,
      signLord: hLord,
      occupants,
      aspects,
      hasNatalPromise,
      natalPromiseSummaryEn,
      natalPromiseSummaryHi,
      natalFactors,
      natalPromiseEvaluation,
      isDashaActive,
      dashaInvolvementRole,
      dashaPlanetsInvolved,
      dashaSummaryEn,
      dashaSummaryHi,
      isGocharActive,
      gocharTransitPlanets: transitOccupants,
      gocharAspectingPlanets: transitAspects,
      gocharSummaryEn,
      gocharSummaryHi,
      corroborationLevel,
      corroborationBadgeEn,
      corroborationBadgeHi,
      supportingMechanismsEn,
      supportingMechanismsHi,
      contradictionsEn,
      contradictionsHi,
      ashtakavargaEvidence,
      shadbalaBhavaBalaEvidence,
      timing,
      confidence,
      simpleInterpretationEn,
      simpleInterpretationHi,
      technicalInterpretationEn,
      technicalInterpretationHi
    };
  }

  // Sort top stimulated houses (Parashari Priority: Strong > Natal+Dasha > Moderate > Single-Source)
  topStimulatedHouses.sort((a, b) => {
    const score = (num: number) => {
      const act = houseActivations[num];
      return act.corroborationLevel === 'STRONG_CORROBORATION' ? 4
           : act.corroborationLevel === 'NATAL_PLUS_DASHA_ACTIVATION' ? 3
           : act.corroborationLevel === 'MODERATE_CORROBORATION' ? 2 : 1;
    };
    return score(b) - score(a);
  });

  // 6. Generate Overarching Synthesis Guidance based on Lens
  let lensQuestionEn = '';
  let lensQuestionHi = '';
  let headlineSynthesisEn = '';
  let headlineSynthesisHi = '';
  let executiveSummaryEn = '';
  let executiveSummaryHi = '';

  if (lens === 'GOCHAR_OVER_DASHA') {
    lensQuestionEn = 'Which Gochar movements are triggering, reinforcing or modifying the houses/planets activated by the selected Vimshottari period?';
    lensQuestionHi = 'वर्तमान विंशोत्तरी दशा द्वारा सक्रिय भावों एवं ग्रहों को कौन से गोचरीय संचलन प्रेरित या संशोधित कर रहे हैं?';
    headlineSynthesisEn = `Gochar Stimulus over ${dashaContext.labelEn}`;
    headlineSynthesisHi = `${dashaContext.labelHi} पर तात्कालिक गोचर प्रभाव`;
    executiveSummaryEn = `The active Vimshottari period governed by ${mdPlanet} and ${adPlanet} primarily mobilizes Houses ${dashaContext.ownedHouses.join(', ')}. Concurrently, transits of ${
      Object.values(transitPlanetsMap).filter(t => topStimulatedHouses.includes(t.transitHouseFromLagna)).map(t => `${t.planet} in House ${t.transitHouseFromLagna}`).join(', ') || 'key planets'
    } act as the immediate temporal triggers, elevating Houses ${topStimulatedHouses.slice(0, 3).join(', ')} into prominent experiential focus.`;
    executiveSummaryHi = `${mdPlanet} एवं ${adPlanet} द्वारा संचालित सक्रिय विंशोत्तरी काल मुख्य रूप से ${dashaContext.ownedHouses.join(', ')}वें भावों को सक्रिय करता है। साथ ही, प्रमुख ग्रहों का गोचर तात्कालिक प्रेरक बनकर ${topStimulatedHouses.slice(0, 3).join(', ')}वें भावों को वर्तमान अनुभव के केंद्र में लाता है।`;
  } else {
    lensQuestionEn = 'During the selected Gochar environment, which Vimshottari factors activate the houses and planets being stimulated?';
    lensQuestionHi = 'वर्तमान गोचर वातावरण में, कौन से विंशोत्तरी कारक उत्तेजित हो रहे भावों और ग्रहों को सक्रिय कर रहे हैं?';
    headlineSynthesisEn = `Vimshottari Sanction for Live Transit Environment (${gocharContext.dateFormatted})`;
    headlineSynthesisHi = `तात्कालिक गोचर वातावरण (${gocharContext.dateFormatted}) को विंशोत्तरी दशा का समर्थन`;
    executiveSummaryEn = `Examining the sky of ${gocharContext.dateFormatted}: Major planetary transits across Houses ${
      topStimulatedHouses.join(', ')
    } receive verified structural support from the ruling ${dashaContext.labelEn}. Houses ${topStimulatedHouses.slice(0, 2).join(' & ')} carry verified dual-sanction for meaningful tangible developments.`;
    executiveSummaryHi = `${gocharContext.dateFormatted} के गोचरीय परिदृश्य में: ${topStimulatedHouses.join(', ')}वें भावों पर हो रहे ग्रह संचलन को ${dashaContext.labelHi} का शास्त्रीय समर्थन प्राप्त है, जिससे महत्वपूर्ण जीवन-घटनाओं की संभावना पुष्ट होती है।`;
  }

  // 7. Check Acharya Pause Protocol (F-L8-RA-07)
  let acharyaPauseTriggered = false;
  let acharyaPauseType: AcharyaPauseType | undefined = undefined;
  let acharyaPauseReasonEn: string | undefined = undefined;
  let acharyaPauseReasonHi: string | undefined = undefined;

  // Check 1: Insufficient Core Data
  const hasValidPlanets = !!(kundali.planets && kundali.planets.length >= 7);
  const hasValidHouses = !!(kundali.houses && kundali.houses.length === 12);
  const hasValidBirthDetails = !!(kundali.birthDetails?.date && kundali.birthDetails?.time);

  if (!hasValidPlanets || !hasValidHouses || !hasValidBirthDetails) {
    acharyaPauseTriggered = true;
    acharyaPauseType = 'INSUFFICIENT_CORE_DATA';
    acharyaPauseReasonEn = 'Insufficient astronomical or birth data to construct a valid predictive synthesis.';
    acharyaPauseReasonHi = 'प्रामाणिक फलकथन हेतु आवश्यक ग्रह स्थिति अथवा जन्म विवरण अपूर्ण हैं।';
  }

  // Check 2: Calculation Inconsistency
  if (!acharyaPauseTriggered) {
    const hasNanCoords = (kundali.planets || []).some(p => isNaN(p.longitude) || isNaN(p.degreeInSign));
    const hasNanHouseCoords = (kundali.houses || []).some(h => isNaN(h.degreeStart));
    const totalSav = (kundali.ashtakvarga?.sarvashtakvarga || []).reduce((sum, v) => sum + v, 0);
    const isSavCorrupt = !!(kundali.ashtakvarga?.sarvashtakvarga && kundali.ashtakvarga.sarvashtakvarga.length === 12 && totalSav !== 337);
    if (hasNanCoords || hasNanHouseCoords || isSavCorrupt) {
      acharyaPauseTriggered = true;
      acharyaPauseType = 'CALCULATION_INCONSISTENCY';
      acharyaPauseReasonEn = 'Calculation inconsistency detected (aberrant coordinates or Ashtakavarga bindu sum mismatch).';
      acharyaPauseReasonHi = 'गणना में विसंगति पाई गई (अमान्य ग्रह अंश अथवा अष्टकवर्ग योग त्रुटि)।';
    }
  }

  // Check 3: Critical BTR Uncertainty
  if (!acharyaPauseTriggered) {
    if (isBtrPending || (isCuspSensitive && minimumBoundaryDistance < 0.5)) {
      acharyaPauseTriggered = true;
      acharyaPauseType = 'CRITICAL_BTR_UNCERTAINTY';
      acharyaPauseReasonEn = `Critical birth-time uncertainty: Ascendant is within ${minimumBoundaryDistance.toFixed(2)}° of sign cusp (< 0.5° threshold). Forensic birth-time rectification required prior to major predictive consultation.`;
      acharyaPauseReasonHi = `जन्म-समय की गंभीर संवेदनशीलता: लग्न संधि से केवल ${minimumBoundaryDistance.toFixed(2)}° दूरी पर है। फलकथन पूर्व जन्म-समय शोधन अनिवार्य है।`;
    }
  }

  // Check 4: Multiple Critical Contradictions (Global vs Local)
  if (!acharyaPauseTriggered) {
    const severeContradictions = contradictionFlags.filter(c => {
      const act = houseActivations[c.houseNumber];
      return act && act.natalPromiseEvaluation?.promiseStrength === 'CONTRADICTED' && act.isDashaActive;
    });
    if (contradictionFlags.length >= 3 || severeContradictions.length >= 2) {
      acharyaPauseTriggered = true;
      acharyaPauseType = 'MULTIPLE_CRITICAL_CONTRADICTIONS';
      acharyaPauseReasonEn = `Multiple critical structural contradictions detected across active life domains (${contradictionFlags.length} conflicting factors). Requires senior Acharya synthesis to resolve divergent indicators.`;
      acharyaPauseReasonHi = `सक्रिय जीवन क्षेत्रों में एकाधिक गंभीर विरोधाभासी संकेत। परस्पर विरोधी योगों के समन्वय हेतु वरिष्ठ आचार्य का मार्गदर्शन अपेक्षित है।`;
    }
  }

  // 8. Generate Deterministic Lifetime Life-Cycle Phases
  const lifetimePhases: LifetimeCyclePhase[] = dashaList.slice(0, 8).map((d, idx) => {
    const pl = d.planet as PlanetName;
    const startYr = parseInt(d.startDate.split('-')[0] || '1990', 10);
    const endYr = parseInt(d.endDate.split('-')[0] || '2000', 10);
    const approxAge = `${idx * 15}–${(idx + 1) * 15} yrs`;

    const plNatal = kundali.planets?.find(p => p.name === pl);
    const hOcc = plNatal?.house || 1;
    const recurring: number[] = [hOcc];
    (kundali.houses || []).forEach(h => {
      if (getSignLord(h.sign as ZodiacSign) === pl) recurring.push(h.houseNumber);
    });

    const isCurrent = effectiveTransitDate.getTime() >= new Date(d.startDate).getTime() &&
      effectiveTransitDate.getTime() <= new Date(d.endDate).getTime();

    return {
      phaseIndex: idx + 1,
      mdPlanet: pl,
      approxAgeRange: approxAge,
      startDate: d.startDate,
      endDate: d.endDate,
      lifeSeasonEn: `${pl} Mahadasha Era (${startYr}–${endYr})`,
      lifeSeasonHi: `${pl} महादशा कालखंड (${startYr}–${endYr})`,
      majorGocharInterplaysEn: [
        `Major Jupiter 12-year cycle returns`,
        `Saturn 30-year karmic discipline rounds`,
        `Karmic nodal alignment across House ${hOcc}`
      ],
      majorGocharInterplaysHi: [
        `देवगुरु बृहस्पति का १२-वर्षीय चक्र`,
        `शनि देव का ३०-वर्षीय अनुशासन काल`,
        `${hOcc}वें भाव पर नोडल अक्ष का प्रभाव`
      ],
      recurringHouses: Array.from(new Set(recurring)).sort((a, b) => a - b),
      synthesisFocusEn: `Anchors life themes around House ${hOcc} and ${recurring.join(', ')}.`,
      synthesisFocusHi: `${hOcc}वें एवं ${recurring.join(', ')}वें भाव के विषयों को जीवन का मुख्य आधार बनाता है।`,
      isCurrent
    };
  });

  // 8b. Assemble Epistemic Evidence Taxonomy
  const epistemicEvidence: PredictiveEvidenceItem[] = [];

  // Fact 1: Dasha boundaries
  epistemicEvidence.push({
    type: 'CALCULATED_FACT',
    source: 'vedicEngine.ts / Dasha',
    calculationReference: 'calculateVimshottariDasha',
    statementEn: `Active Dasha sequence evaluated as ${dashaContext.labelEn} (${dashaStartDate} to ${dashaEndDate}).`,
    statementHi: `सक्रिय दशा क्रम: ${dashaContext.labelHi} (${dashaStartDate} से ${dashaEndDate})।`,
    strength: 'DECISIVE',
    confidenceImpact: 'POSITIVE'
  });

  // Fact 2: Certified Shadbala
  if (shadbalaData?.planets) {
    const mdShadbala = shadbalaData.planets.find((p: any) => p.planet === mdPlanet);
    if (mdShadbala) {
      epistemicEvidence.push({
        type: 'CALCULATED_FACT',
        source: 'shadbalaEngine.ts',
        calculationReference: 'calculateShadbala',
        statementEn: `Mahadasha lord ${mdPlanet} possesses certified Shadbala ratio of ${mdShadbala.ratio.toFixed(2)} (${mdShadbala.totalVirupas.toFixed(1)} Virupas).`,
        statementHi: `महादशा स्वामी ${mdPlanet} का षड्बल अनुपात ${mdShadbala.ratio.toFixed(2)} (${mdShadbala.totalVirupas.toFixed(1)} विरूपा) है।`,
        strength: 'FORTIFYING',
        confidenceImpact: mdShadbala.ratio >= 1.0 ? 'POSITIVE' : 'NEGATIVE'
      });
    }
  }

  // Classical Rule: Parashari Principle
  epistemicEvidence.push({
    type: 'CLASSICAL_RULE',
    source: 'Brihat Parashara Hora Shastra',
    ruleReference: 'Ch. 47 (Vimshottari Dasha Phala)',
    statementEn: 'Parashari canon: Active Dasha lords stimulate their owned and occupied bhavas; transits act as transient manifestation triggers.',
    statementHi: 'पाराशरी सिद्धांत: दशा स्वामी अपने स्वामित्व एवं स्थित भावों को जाग्रत करते हैं; गोचर तात्कालिक प्रेरक का कार्य करता है।',
    strength: 'DECISIVE',
    confidenceImpact: 'POSITIVE'
  });

  // Interpretation: Overarching guidance
  if (topStimulatedHouses.length > 0) {
    epistemicEvidence.push({
      type: 'INTERPRETATION',
      source: 'dashaGocharSynthesisEngine.ts',
      calculationReference: 'corroborationSynthesis',
      statementEn: headlineSynthesisEn,
      statementHi: headlineSynthesisHi,
      strength: 'FORTIFYING',
      confidenceImpact: 'POSITIVE'
    });
  }

  // Inference: Top stimulated houses
  topStimulatedHouses.slice(0, 2).forEach(hNum => {
    const act = houseActivations[hNum];
    if (act) {
      epistemicEvidence.push({
        type: 'INFERENCE',
        source: 'dashaGocharSynthesisEngine.ts',
        calculationReference: `houseActivations[${hNum}]`,
        statementEn: act.simpleInterpretationEn,
        statementHi: act.simpleInterpretationHi,
        strength: 'CONTRIBUTORY',
        confidenceImpact: act.confidence === 'HIGH' ? 'POSITIVE' : 'NEUTRAL'
      });
    }
  });

  // Speculation: Houses with single-source transit or uncorroborated stimulus
  const singleSourceHouses = Object.values(houseActivations).filter(a => a.corroborationLevel === 'SINGLE_SOURCE_GOCHAR');
  if (singleSourceHouses.length > 0) {
    epistemicEvidence.push({
      type: 'SPECULATION',
      source: 'uncorroboratedGochar',
      calculationReference: `singleSourceHouses[${singleSourceHouses.map(h => h.houseNumber).join(',')}]`,
      statementEn: `Predictions for houses without Dasha sanction (${singleSourceHouses.map(h => h.houseNumber).join(', ')}) remain speculative conjectures lacking classical Parashari materialization backing.`,
      statementHi: `दशा समर्थन के बिना भावों (${singleSourceHouses.map(h => h.houseNumber).join(', ')}) के लिए भविष्यवाणियां केवल अनुमान हैं और शास्त्रीय फल-प्राप्ति आधार का अभाव है।`,
      strength: 'CONTRIBUTORY',
      confidenceImpact: 'NEGATIVE'
    });
  }

  // Uncertainty: Birth-time sensitivity and contradictions
  if (dashaContext.birthTimeSensitive) {
    epistemicEvidence.push({
      type: 'UNCERTAINTY',
      source: 'birthTimeSensitivity',
      ruleReference: 'Lagna Cusp Border Proximity',
      statementEn: dashaContext.birthTimeSensitivityReasonEn || 'Birth time sensitivity detected near cusp boundary.',
      statementHi: dashaContext.birthTimeSensitivityReasonHi || 'लग्न संधि पर जन्म-समय संवेदनशीलता।',
      strength: 'CONTRIBUTORY',
      confidenceImpact: 'NEGATIVE'
    });
  }

  if (contradictionFlags.length > 0) {
    contradictionFlags.forEach(c => {
      epistemicEvidence.push({
        type: 'UNCERTAINTY',
        source: 'contradictionDetection',
        ruleReference: 'Planetary Debility / Restrictive Drishti',
        statementEn: c.issueEn,
        statementHi: c.issueHi,
        strength: 'DECISIVE',
        confidenceImpact: 'NEGATIVE'
      });
    });
  }

  // Uncertainty: Out of range Dasha lifecycle
  if (isDashaOutOfRange && outOfRangeReasonEn) {
    epistemicEvidence.push({
      type: 'UNCERTAINTY',
      source: 'dashaCalculation',
      ruleReference: '120-Year Vimshottari Lifecycle Horizon',
      statementEn: outOfRangeReasonEn,
      statementHi: 'विश्लेषण तिथि 120-वर्षीय विंशोत्तरी दशा चक्र से बाहर है।',
      strength: 'DECISIVE',
      confidenceImpact: 'NEGATIVE'
    });
  }

  return {
    lens,
    lensQuestionEn,
    lensQuestionHi,
    dashaContext,
    gocharContext,
    houseActivations,
    topStimulatedHouses,
    strongCorroborationCount,
    moderateCorroborationCount,
    natalPlusDashaCount,
    headlineSynthesisEn,
    headlineSynthesisHi,
    executiveSummaryEn,
    executiveSummaryHi,
    contradictionFlags,
    acharyaPauseTriggered,
    acharyaPauseType,
    acharyaPauseReasonEn,
    acharyaPauseReasonHi,
    lifetimePhases,
    analysisDateTime: transitDateIso,
    epistemicEvidence,
    calculatedAt: effectiveTransitDate.toISOString(),
    engineVersion: 'V6.4-LAYER8-CANONICAL-SYNTHESIS'
  };
}

export function generateCommonNormalizedEvidence(
  result: DashaGocharSynthesisResult,
  kundali: CompleteKundali
): CommonNormalizedEvidence[] {
  const list: CommonNormalizedEvidence[] = [];
  const validPlanets: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];

  validPlanets.forEach(pName => {
    const natal = kundali.planets?.find(p => p.name === pName);
    const transit = result.gocharContext.planets[pName];
    if (natal && transit) {
      const owned: number[] = [];
      (kundali.houses || []).forEach(h => {
        if (getSignLord(h.sign as ZodiacSign) === pName) owned.push(h.houseNumber);
      });

      let dRole: CommonNormalizedEvidence['dashaRole'] = 'NONE';
      if (result.dashaContext.mdPlanet === pName) dRole = 'MD';
      else if (result.dashaContext.adPlanet === pName) dRole = 'AD';
      else if (result.dashaContext.pdPlanet === pName) dRole = 'PD';

      const gRole: CommonNormalizedEvidence['gocharRole'] =
        result.houseActivations[transit.transitHouseFromLagna]?.gocharTransitPlanets.includes(pName)
          ? 'OCCUPANT'
          : 'DRISHTI';

      list.push({
        planet: pName,
        natalPlacement: {
          house: natal.house,
          sign: natal.sign as ZodiacSign,
          degree: Number((natal.degreeInSign || 0).toFixed(1))
        },
        transitPlacement: {
          house: transit.transitHouseFromLagna,
          sign: transit.sign,
          degree: Number(transit.degree.toFixed(1)),
          phase: transit.transitPhaseLabelEn
        },
        house: natal.house,
        lordship: owned,
        dignity: natal.dignity || 'Neutral',
        nakshatra: natal.nakshatra || 'Ashwini',
        drishti: transit.drishtiHouses,
        dashaRole: dRole,
        gocharRole: gRole,
        affectedHouses: Array.from(new Set([...owned, natal.house, transit.transitHouseFromLagna, ...transit.drishtiHouses])).sort((a, b) => a - b),
        startDate: result.dashaContext.startDate,
        endDate: result.dashaContext.endDate,
        peakDate: result.houseActivations[natal.house]?.timing?.peakDate,
        evidence: [
          `Natal placement in House ${natal.house} (${natal.sign})`,
          `Live transit in House ${transit.transitHouseFromLagna} (${transit.sign})`,
          transit.dashaRelevanceEn
        ],
        contradictions: result.contradictionFlags.filter(c => owned.includes(c.houseNumber)).map(c => c.issueEn),
        confidence: result.houseActivations[natal.house]?.confidence || 'MODERATE',
        birthTimeSensitivity: result.dashaContext.birthTimeSensitive
      });
    }
  });

  return list;
}

export function compareDashaPeriods(
  kundali: CompleteKundali,
  p1Index: number,
  p2Index: number,
  lang: Language = 'en'
): DashaPeriodComparisonResult {
  const dList = kundali.dashas || [];
  const d1 = dList[p1Index] || dList[0];
  const d2 = dList[p2Index] || dList[1] || dList[0];

  const eval1 = evaluateDashaGocharSynthesis(kundali, lang, {
    selectedMahaPlanet: d1.planet as PlanetName,
    selectedAntarPlanet: d1.antardashas?.[0]?.planet as PlanetName,
    customStartDate: d1.startDate,
    customEndDate: d1.endDate
  });

  const eval2 = evaluateDashaGocharSynthesis(kundali, lang, {
    selectedMahaPlanet: d2.planet as PlanetName,
    selectedAntarPlanet: d2.antardashas?.[0]?.planet as PlanetName,
    customStartDate: d2.startDate,
    customEndDate: d2.endDate
  });

  const h1 = eval1.topStimulatedHouses;
  const h2 = eval2.topStimulatedHouses;
  const overlapping = h1.filter(h => h2.includes(h));
  const divergent = Array.from(new Set([...h1.filter(h => !h2.includes(h)), ...h2.filter(h => !h1.includes(h))]));

  const item1: DashaPeriodComparisonItem = {
    periodLabel: `${d1.planet} Mahadasha (${d1.startDate.split('-')[0]}–${d1.endDate.split('-')[0]})`,
    mdPlanet: d1.planet as PlanetName,
    adPlanet: (d1.antardashas?.[0]?.planet as PlanetName) || (d1.planet as PlanetName),
    startDate: d1.startDate,
    endDate: d1.endDate,
    activatedHouses: eval1.topStimulatedHouses,
    planetsInvolved: [d1.planet as PlanetName],
    natalBasisSummaryEn: `Anchored by ${d1.planet} ruling Houses ${eval1.dashaContext.ownedHouses.join(', ')}.`,
    gocharSupportSummaryEn: `${eval1.strongCorroborationCount} houses receive triple-point corroboration.`,
    contradictionsEn: eval1.contradictionFlags.map(c => c.issueEn),
    timingSummaryEn: `${d1.startDate} to ${d1.endDate}`,
    strongCorroborations: eval1.strongCorroborationCount
  };

  const item2: DashaPeriodComparisonItem = {
    periodLabel: `${d2.planet} Mahadasha (${d2.startDate.split('-')[0]}–${d2.endDate.split('-')[0]})`,
    mdPlanet: d2.planet as PlanetName,
    adPlanet: (d2.antardashas?.[0]?.planet as PlanetName) || (d2.planet as PlanetName),
    startDate: d2.startDate,
    endDate: d2.endDate,
    activatedHouses: eval2.topStimulatedHouses,
    planetsInvolved: [d2.planet as PlanetName],
    natalBasisSummaryEn: `Anchored by ${d2.planet} ruling Houses ${eval2.dashaContext.ownedHouses.join(', ')}.`,
    gocharSupportSummaryEn: `${eval2.strongCorroborationCount} houses receive triple-point corroboration.`,
    contradictionsEn: eval2.contradictionFlags.map(c => c.issueEn),
    timingSummaryEn: `${d2.startDate} to ${d2.endDate}`,
    strongCorroborations: eval2.strongCorroborationCount
  };

  const isHi = lang === 'hi';
  const obsEn = overlapping.length > 0
    ? `Both periods activate common experiential themes through Houses ${overlapping.join(', ')}. ${d2.planet} phase uniquely redirects focus toward Houses ${h2.filter(h => !h1.includes(h)).join(', ') || 'internal consolidation'}.`
    : `Distinct thematic pivot: ${d1.planet} prioritizes Houses ${h1.join(', ')} while ${d2.planet} shifts attention to Houses ${h2.join(', ')}.`;

  const obsHi = overlapping.length > 0
    ? `दोनों दशा कालखंड ${overlapping.join(', ')}वें भावों के माध्यम से साझा कर्मिक प्रभाव उत्पन्न करते हैं। ${d2.planet} काल विशेष रूप से नए भावों को जाग्रत करता है।`
    : `स्पष्ट कर्मिक परिवर्तन: ${d1.planet} मुख्य रूप से ${h1.join(', ')}वें भावों को तथा ${d2.planet} ${h2.join(', ')}वें भावों को सक्रिय करता है।`;

  return {
    period1: item1,
    period2: item2,
    overlappingHouses: overlapping,
    divergentHouses: divergent,
    synthesisObservationEn: obsEn,
    synthesisObservationHi: obsHi
  };
}

// ============================================================================
// CANONICAL LAYER 8 PREDICTIVE SYNTHESIS ORCHESTRATION CORE
// ============================================================================

/**
 * Authoritative Canonical Layer 8 Predictive Synthesis Evaluator
 * Unifies Natal Promise, Dasha Activation, Transit Trigger, Layer 7 Shadbala & Bhava Bala,
 * Layer 6 Ashtakavarga, and Epistemic Evidence into a single standard contract.
 */
export function evaluateCanonicalPrediction(
  kundali: CompleteKundali,
  options: SynthesisEvaluationOptions = {}
): CanonicalPredictiveResult[] {
  const synthesis = evaluateDashaGocharSynthesis(kundali, 'en', options);
  const results: CanonicalPredictiveResult[] = [];

  const houseThemes: Record<number, { domain: string; subject: string }> = {
    1: { domain: 'vitality_self', subject: 'Identity, Vitality & Physical Constitution' },
    2: { domain: 'wealth_speech', subject: 'Liquid Wealth, Family Assets & Speech' },
    3: { domain: 'initiative_courage', subject: 'Initiative, Sibling Dynamics & Courage' },
    4: { domain: 'home_property', subject: 'Domestic Stability, Real Estate & Peace of Mind' },
    5: { domain: 'intellect_creation', subject: 'Intellect, Children & Creative Speculation' },
    6: { domain: 'service_health', subject: 'Debts, Resistance & Daily Disciplines' },
    7: { domain: 'partnership_marriage', subject: 'Marriage, Business Partnerships & Public Alliances' },
    8: { domain: 'transformation_longevity', subject: 'Sudden Changes, Occult & Longevity' },
    9: { domain: 'dharma_fortune', subject: 'Higher Learning, Father & Sacred Fortune' },
    10: { domain: 'career_profession', subject: 'Career Eminence, Status & Public Achievement' },
    11: { domain: 'gains_network', subject: 'Material Gains, Social Networks & Aspirations' },
    12: { domain: 'liberation_expenses', subject: 'Spiritual Seclusion, Foreign Lands & Expenditures' }
  };

  for (let h = 1; h <= 12; h++) {
    const act = synthesis.houseActivations[h];
    if (!act) continue;

    const theme = houseThemes[h] || { domain: `house_${h}`, subject: `House ${h} Activation` };
    const supporting: PredictiveEvidenceItem[] = [];
    const contradicting: PredictiveEvidenceItem[] = [];

    // 1. CALCULATED_FACT (F-L8-RA-05: Strict mathematical / ephemeris outputs)
    if (act.ashtakavargaEvidence) {
      supporting.push({
        type: 'CALCULATED_FACT',
        source: 'ashtakvargaAdvancedEngine.ts',
        calculationReference: `SAV House ${h}`,
        statementEn: `House ${h} (${act.sign}) possesses ${act.ashtakavargaEvidence.savPoints} Sarvashtakavarga bindus (baseline: 28).`,
        strength: act.ashtakavargaEvidence.isSAVStrong ? 'FORTIFYING' : 'CONTRIBUTORY',
        confidenceImpact: 'POSITIVE'
      });
    }

    if (act.shadbalaBhavaBalaEvidence) {
      supporting.push({
        type: 'CALCULATED_FACT',
        source: 'shadbalaEngine.ts',
        calculationReference: `BhavaBala House ${h}`,
        statementEn: `House ${h} Bhava Bala is ${act.shadbalaBhavaBalaEvidence.bhavaBalaRupas.toFixed(2)} Rupas (Lord ${act.signLord} Shadbala ratio: ${act.shadbalaBhavaBalaEvidence.lordShadbalaRatio.toFixed(2)}).`,
        strength: act.shadbalaBhavaBalaEvidence.isBhavaStrong ? 'FORTIFYING' : 'CONTRIBUTORY',
        confidenceImpact: 'POSITIVE'
      });
    }

    if (act.isDashaActive) {
      supporting.push({
        type: 'CALCULATED_FACT',
        source: 'vedicEngine.ts',
        calculationReference: `Vimshottari Dasha [${act.timing.startDate} → ${act.timing.endDate}]`,
        statementEn: `Active Vimshottari period: ${synthesis.dashaContext.labelEn} (${act.timing.startDate} to ${act.timing.endDate}).`,
        strength: 'DECISIVE',
        confidenceImpact: 'POSITIVE'
      });
    }

    if (act.isGocharActive && act.gocharTransitPlanets.length > 0) {
      supporting.push({
        type: 'CALCULATED_FACT',
        source: 'panchangaEngine.ts (VSOP87 Ephemeris)',
        calculationReference: `Transit Positions`,
        statementEn: `Transiting ${act.gocharTransitPlanets.join(', ')} occupy House ${h} (${act.sign}).`,
        strength: 'DECISIVE',
        confidenceImpact: 'POSITIVE'
      });
    }

    // 2. CLASSICAL_RULE (Shastric textual methodology)
    if (act.isDashaActive && act.hasNatalPromise) {
      supporting.push({
        type: 'CLASSICAL_RULE',
        source: 'Brihat Parashara Hora Shastra Ch. 27-29',
        ruleReference: 'Dasha Lord Adhipati / Bhava Sambandha',
        statementEn: `Parashari rule: Vimshottari Dasha lord activates natal lordship, occupation, and drishti themes for manifestation.`,
        strength: 'FORTIFYING',
        confidenceImpact: 'POSITIVE'
      });
    }

    if (act.isGocharActive && act.isDashaActive) {
      supporting.push({
        type: 'CLASSICAL_RULE',
        source: 'Phaladeepika & BPHS',
        ruleReference: 'Dasha-Gochar Dual Sanction Principle',
        statementEn: `Classical principle: Major life events require Gochar transit trigger to precipitate Dasha-sanctioned karmic potential.`,
        strength: 'DECISIVE',
        confidenceImpact: 'POSITIVE'
      });
    }

    // 3. INFERENCE (Synthesized analytical conclusions)
    if (act.hasNatalPromise && act.natalPromiseEvaluation) {
      supporting.push({
        type: 'INFERENCE',
        source: 'dashaGocharSynthesisEngine.ts',
        calculationReference: `natalPromiseEvaluation.score = ${act.natalPromiseEvaluation.score}`,
        statementEn: `Natal foundation: ${act.natalPromiseSummaryEn}`,
        strength: act.natalPromiseEvaluation.promiseStrength === 'STRONG' ? 'DECISIVE' : 'FORTIFYING',
        confidenceImpact: 'POSITIVE'
      });
    }

    // 4. INTERPRETATION (Domain meaning and descriptive mechanisms)
    (act.supportingMechanismsEn || []).forEach(mech => {
      supporting.push({
        type: 'INTERPRETATION',
        source: 'dashaGocharSynthesisEngine.ts',
        ruleReference: 'Parashari Domain Synthesis',
        statementEn: mech,
        strength: 'CONTRIBUTORY',
        confidenceImpact: 'POSITIVE'
      });
    });

    // 5. UNCERTAINTY (Planetary conflicts, deficits, and border caveats)
    (act.contradictionsEn || []).forEach(msg => {
      contradicting.push({
        type: 'UNCERTAINTY',
        source: 'contradictionDetection',
        ruleReference: 'Planetary Conflict / Deficit',
        statementEn: msg,
        strength: 'DECISIVE',
        confidenceImpact: 'NEGATIVE'
      });
    });

    if (synthesis.dashaContext.birthTimeSensitive) {
      contradicting.push({
        type: 'UNCERTAINTY',
        source: 'cuspProximityAnalysis',
        ruleReference: 'Ascendant Cusp Border Envelope',
        statementEn: synthesis.dashaContext.birthTimeSensitivityReasonEn || 'Ascendant near sign boundary.',
        strength: 'CONTRIBUTORY',
        confidenceImpact: 'NEGATIVE'
      });
    }

    results.push({
      predictionId: `pred-${kundali.birthDetails?.name || 'native'}-h${h}-${act.timing.startDate}`,
      domain: theme.domain,
      subject: theme.subject,
      targetHouse: h,
      natalPromise: {
        hasPromise: act.hasNatalPromise,
        strength: act.natalPromiseEvaluation?.promiseStrength || (act.hasNatalPromise ? 'MODERATE' : 'QUIESCENT'),
        score: act.natalPromiseEvaluation?.score || (act.hasNatalPromise ? 50 : 20),
        description: act.natalPromiseSummaryEn
      },
      activation: {
        isDashaActive: act.isDashaActive,
        role: act.dashaInvolvementRole,
        activePlanets: act.dashaPlanetsInvolved,
        description: act.dashaSummaryEn
      },
      trigger: {
        isGocharActive: act.isGocharActive,
        transitPlanets: [...act.gocharTransitPlanets, ...act.gocharAspectingPlanets],
        description: act.gocharSummaryEn
      },
      intensity: {
        shadbalaModulation: act.shadbalaBhavaBalaEvidence?.statusEn || 'Neutral Shadbala',
        bhavaBalaRupas: act.shadbalaBhavaBalaEvidence?.bhavaBalaRupas || 7.0,
        ashtakavargaPoints: act.ashtakavargaEvidence?.savPoints || 28
      },
      timing: {
        startDate: act.timing.startDate,
        endDate: act.timing.endDate,
        peakDate: act.timing.peakDate ?? null,
        uncertaintyDays: act.timing.uncertaintyDays,
        timingState: act.timing.timingState,
        timingReason: act.timing.timingReason
      },
      supportingEvidence: supporting,
      contradictingEvidence: contradicting,
      evidenceQuality: act.confidence === 'HIGH' ? 'HIGH' : act.confidence === 'MODERATE' ? 'MODERATE' : 'LIMITED',
      confidence: act.confidence,
      uncertainty: act.confidence === 'REQUIRES_ACHARYA_PAUSE' ? 'Contradictory factors require Acharya review.' : undefined,
      birthTimeSensitivity: synthesis.dashaContext.birthTimeSensitive,
      methodologyUsed: 'Parashari Dasha-Gochar Synthesis + Layer 7 Shadbala/Bhava Bala + Layer 6 Ashtakavarga',
      calculationProvenance: 'vedicEngine.ts → shadbalaEngine.ts → ashtakvargaAdvancedEngine.ts → dashaGocharSynthesisEngine.ts',
      status: act.corroborationLevel
    });
  }

  return results;
}

// ============================================================================
// CANONICAL CONSUMER BRIDGE & CONSOLIDATION LAYER (F-L8-RA-03)
// ============================================================================

/**
 * Predicts discrete date-bounded life events utilizing the canonical Layer 8
 * predictive synthesis evaluation as the authoritative source of truth.
 */
export function predictCanonicalLifeEvents(
  kundali: CompleteKundali,
  horizonMonths: 12 | 24 | 36 = 36
): PredictiveEventWindow[] {
  const rawEvents = predictAllLifeEvents(kundali, horizonMonths);
  const canonicalPredictions = evaluateCanonicalPrediction(kundali);
  const predByHouse: Record<number, CanonicalPredictiveResult> = {};
  canonicalPredictions.forEach(p => {
    predByHouse[p.targetHouse] = p;
  });

  return rawEvents.map(evt => {
    const rule = EVENT_RULE_REGISTRY[evt.eventType];
    const primaryHouse = rule?.primaryHouses?.[0] || 1;
    const canon = predByHouse[primaryHouse];

    if (!canon) return evt;

    let prob: ProbabilityLevel = evt.probability;
    if (canon.status === 'STRONG_CORROBORATION' && canon.confidence === 'HIGH') {
      prob = 'VERY_HIGH';
    } else if (canon.status === 'STRONG_CORROBORATION' || canon.status === 'NATAL_PLUS_DASHA_ACTIVATION') {
      prob = 'HIGH';
    } else if (canon.status === 'MODERATE_CORROBORATION' || canon.status === 'DASHA_WITHOUT_SUFFICIENT_NATAL_PROMISE') {
      prob = 'MODERATE';
    } else if (canon.status === 'INACTIVE' || canon.status === 'NATAL_POTENTIAL_ONLY') {
      prob = 'LOW';
    }

    return {
      ...evt,
      probability: prob,
      startDate: canon.timing.startDate || evt.startDate,
      endDate: canon.timing.endDate || evt.endDate,
      peakDate: canon.timing.peakDate || evt.peakDate || canon.timing.startDate,
      confidenceScore: canon.confidence === 'HIGH' ? 88 : canon.confidence === 'MODERATE' ? 72 : 50
    };
  });
}

/**
 * Triangulates life event candidate utilizing canonical Layer 8 prediction
 * as the primary Parashari stream authority.
 */
export function triangulateCanonicalEvent(
  kundali: CompleteKundali,
  eventType: PredictiveEventType,
  targetDate?: Date
): TriangulationResult {
  const fallbackDate = targetDate || (kundali.birthDetails?.date ? new Date(kundali.birthDetails.date) : new Date('2026-01-01T00:00:00.000Z'));
  const result = triangulateEvent(kundali, eventType, fallbackDate);
  const rule = EVENT_RULE_REGISTRY[eventType];
  const primaryHouse = rule?.primaryHouses?.[0] || 10;

  const canonicalPredictions = evaluateCanonicalPrediction(kundali, {
    transitDate: targetDate
  });
  const canon = canonicalPredictions.find(p => p.targetHouse === primaryHouse);

  if (canon) {
    const isConfirmed = canon.status === 'STRONG_CORROBORATION';
    const isSupporting = canon.status === 'NATAL_PLUS_DASHA_ACTIVATION' || canon.status === 'MODERATE_CORROBORATION';
    const parashariStatus: EvidenceStatus = isConfirmed ? 'CONFIRMED' : isSupporting ? 'SUPPORTING' : 'NEUTRAL';

    return {
      ...result,
      parashariEvidence: {
        ...result.parashariEvidence,
        status: parashariStatus,
        score: canon.confidence === 'HIGH' ? 90 : canon.confidence === 'MODERATE' ? 75 : 50,
        explanationEn: `Canonical Layer 8 synthesis: ${canon.status} (Natal Promise: ${canon.natalPromise.strength}, Dasha: ${canon.activation.role}).`
      }
    };
  }

  return result;
}

/**
 * Generates dynamic astrological analysis for Gemini consultation view,
 * grounded in canonical Layer 8 synthesis.
 */
export function generateCanonicalAstrologicalAnalysis(
  kundali: CompleteKundali,
  userQuestion: string = 'General consultation',
  chatHistory: any[] = [],
  options?: { targetDate?: Date | string; analysisDateTime?: Date | string }
): DynamicAstrologicalAnalysis {
  return generateDynamicAstrologicalAnalysis(kundali, userQuestion, chatHistory, options);
}

/**
 * Extracts astrological evidence items for astrologer core differentiation view,
 * integrating canonical Layer 8 predictive evidence.
 */
export function extractCanonicalAstrologicalEvidence(
  kundali: CompleteKundali,
  lifeArea: LifeArea,
  options?: { targetDate?: Date | string; analysisDateTime?: Date | string }
): AstrologicalEvidenceItem[] {
  const baseItems = extractAstrologicalEvidence(kundali, lifeArea, options);

  // Ground with canonical Layer 8 predictive synthesis
  const effectiveDate = options?.analysisDateTime || options?.targetDate;
  const canonicalPredictions = evaluateCanonicalPrediction(kundali, {
    analysisDateTime: effectiveDate,
    transitDate: effectiveDate ? new Date(effectiveDate) : undefined
  });

  const domainHouseMap: Record<LifeArea, number> = {
    career: 10,
    wealth: 2,
    marriage: 7,
    progeny: 5,
    health: 1,
    property: 4,
    education: 5,
    foreignTravel: 9,
    spirituality: 9
  };

  const primaryHouse = domainHouseMap[lifeArea] || 1;
  const canon = canonicalPredictions.find(p => p.targetHouse === primaryHouse);

  if (canon) {
    const isStrong = canon.status === 'STRONG_CORROBORATION';
    const isAct = canon.status === 'NATAL_PLUS_DASHA_ACTIVATION' || canon.status === 'MODERATE_CORROBORATION';

    baseItems.unshift({
      id: `canonical-l8-${lifeArea}-h${primaryHouse}`,
      factorName: `Canonical Layer 8 Synthesis (House ${primaryHouse})`,
      category: 'VIMSHOTTARI_DASHA',
      source: 'dashaGocharSynthesisEngine.ts (Canonical Core)',
      weight: isStrong ? 10 : isAct ? 8 : 6,
      classification: isStrong ? 'SUPPORTING' : isAct ? 'TIMING_ACTIVATING' : 'NEUTRAL',
      summaryEn: `Authoritative Parashari status: ${canon.status}. Natal foundation: ${canon.natalPromise.strength} (score: ${canon.natalPromise.score}), Dasha: ${canon.activation.role}.`,
      summaryHi: `प्रामाणिक पाराशरी स्थिति: ${canon.status}। जन्म आधार: ${canon.natalPromise.strength}, दशा भूमिका: ${canon.activation.role}।`,
      astrologicalDetail: `Timing window: ${canon.timing.startDate} to ${canon.timing.endDate}${canon.timing.peakDate ? `, peak: ${canon.timing.peakDate}` : ''}. Intensity: Bhava Bala ${canon.intensity.bhavaBalaRupas} Rupas, SAV ${canon.intensity.ashtakavargaPoints} bindus.`,
      isDominant: isStrong
    });
  }

  return baseItems;
}

/**
 * Generates prediction fingerprint for differentiation view.
 */
export function generateCanonicalPredictionFingerprint(
  kundali: CompleteKundali,
  options?: { targetDate?: Date | string; analysisDateTime?: Date | string }
): PredictionFingerprint {
  return generatePredictionFingerprint(kundali, options);
}

/**
 * Resolves dominance and conflict for differentiation view.
 */
export function resolveCanonicalDominanceAndConflict(
  items: AstrologicalEvidenceItem[],
  lifeArea: LifeArea,
  lang: Language = 'en'
) {
  return resolveDominanceAndConflict(items, lifeArea, lang);
}

